import { useCallback, useEffect, useId, useMemo, useState } from "react";
import axios from "axios";
import { Bell, Book, Bot, ChevronRight, CircleHelp, Clock, Flame, LayoutDashboard, ListFilter, MessageCircle, MessageSquareText, Phone, PhoneCall, PieChart, RefreshCcw, Search, Settings2, ShieldCheck, Trash2, TrendingUp, UserPlus, Users, Webhook, X, Zap } from "lucide-react";
import Simulator from "./pages/Simulator";
import KnowledgeBase from "./pages/KnowledgeBase";
import "@/App.css";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "paushtikk.token";
const USER_KEY = "paushtikk.user";
// Attach Bearer token on every request; redirect to login on 401
axios.interceptors.request.use((cfg) => {
  const token = localStorage.getItem(TOKEN_KEY);
  if (token) cfg.headers = { ...(cfg.headers || {}), Authorization: `Bearer ${token}` };
  return cfg;
});
axios.interceptors.response.use((r) => r, (err) => {
  if (err.response && err.response.status === 401 && !String(err.config?.url || "").endsWith("/auth/login")) {
    localStorage.removeItem(TOKEN_KEY); localStorage.removeItem(USER_KEY);
    if (window.location.pathname !== "/login") window.location.reload();
  }
  return Promise.reject(err);
});
const nav = [{ id: "dashboard", label: "Overview", icon: LayoutDashboard }, { id: "leads", label: "Leads database", icon: Users }, { id: "active", label: "Active customers", icon: ShieldCheck }, { id: "lost", label: "Lost leads", icon: X }, { id: "knowledge", label: "Knowledge base", icon: Book }, { id: "simulator", label: "Lead simulator", icon: Bot }, { id: "webhooks", label: "Webhook events", icon: Webhook }, { id: "team", label: "Team", icon: ShieldCheck, adminOnly: true }];

function Badge({ children, tone = "blue", "data-testid": tid }) { const id = useId(); return <span data-testid={tid || `badge-${String(children).toLowerCase().replaceAll(" ", "-")}-${id.replaceAll(":", "")}`} className={`badge badge-${tone}`}>{children}</span>; }
function scoreTone(score) {
  if (score >= 80) return "green";
  if (score >= 50) return "amber";
  return "slate";
}
function conversionTone(rate) {
  if (rate >= 30) return "green";
  if (rate >= 10) return "amber";
  return "slate";
}
function kbCategoryTone(category) {
  if (category === "plan") return "green";
  if (category === "policy") return "blue";
  if (category === "faq") return "amber";
  return "purple";
}
function roleTone(role) {
  if (role === "ADMIN") return "purple";
  if (role.startsWith("SALES")) return "green";
  return "blue";
}
function relTime(iso) {
  if (!iso) return "—";
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return "—";
  const mins = Math.round((Date.now() - then) / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.round(hrs / 24);
  return `${days}d ago`;
}
function initials(name) { return name.split(" ").map(x => x[0]).join("").slice(0, 2); }

/** Format any numeric amount as INR with Indian digit grouping (lakhs/crores). */
function formatINR(n) {
  const value = Number(n) || 0;
  return `₹${value.toLocaleString("en-IN", { maximumFractionDigits: value >= 1000 ? 0 : 2 })}`;
}
/** Compact INR: ₹85,000 → ₹85K · ₹1,20,000 → ₹1.2L · ₹2,50,00,000 → ₹2.5Cr. */
function formatINRCompact(n) {
  const v = Number(n) || 0;
  if (v >= 10000000) return `₹${(v / 10000000).toFixed(v % 10000000 === 0 ? 0 : 1)}Cr`;
  if (v >= 100000) return `₹${(v / 100000).toFixed(v % 100000 === 0 ? 0 : 1)}L`;
  if (v >= 1000) return `₹${(v / 1000).toFixed(v % 1000 === 0 ? 0 : 1)}K`;
  return formatINR(v);
}
function digitsOnly(phone) { return (phone || "").replace(/\D/g, ""); }
function nudgeMessage(item) {
  const plan = item.plan_interest && item.plan_interest !== "Not captured" ? item.plan_interest.toLowerCase() : "meal plan";
  const first = (item.customer_name || "there").split(" ")[0];
  return `Hi ${first}, this is Paushtikk Switch. Just checking in on your ${plan} enquiry — happy to help you get started. When would be a good time to chat?`;
}
function nudgeUrl(item) { return `https://wa.me/${digitsOnly(item.phone_number)}?text=${encodeURIComponent(nudgeMessage(item))}`; }

const IDENTITY_KEY = "paushtikk.identity";
const DEFAULT_IDENTITY = { name: "Admin", isAdmin: true };
const readIdentity = () => { try { const v = JSON.parse(localStorage.getItem(IDENTITY_KEY)); return v && v.name ? v : DEFAULT_IDENTITY; } catch { return DEFAULT_IDENTITY; } };
const readAuthUser = () => { try { return JSON.parse(localStorage.getItem(USER_KEY)) || null; } catch { return null; } };

export default function App() {
  const [authUser, setAuthUser] = useState(readAuthUser);
  const [checkingAuth, setCheckingAuth] = useState(!!localStorage.getItem(TOKEN_KEY));
  useEffect(() => {
    if (!localStorage.getItem(TOKEN_KEY)) { setCheckingAuth(false); return; }
    axios.get(`${API}/auth/me`).then(r => { setAuthUser(r.data); localStorage.setItem(USER_KEY, JSON.stringify(r.data)); }).catch(() => { setAuthUser(null); localStorage.removeItem(TOKEN_KEY); localStorage.removeItem(USER_KEY); }).finally(() => setCheckingAuth(false));
  }, []);
  const handleLogin = (user, token) => { localStorage.setItem(TOKEN_KEY, token); localStorage.setItem(USER_KEY, JSON.stringify(user)); setAuthUser(user); };
  const handleLogout = () => { localStorage.removeItem(TOKEN_KEY); localStorage.removeItem(USER_KEY); setAuthUser(null); };
  if (checkingAuth) return <div className="auth-loading" data-testid="auth-loading">Signing you in…</div>;
  if (!authUser) return <Login onLogin={handleLogin} />;
  return <Workspace authUser={authUser} onLogout={handleLogout} />;
}

function Login({ onLogin }) {
  const [email, setEmail] = useState("admin@paushtikk.com");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setBusy(true); setError("");
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password });
      onLogin(data.user, data.token);
    } catch (err) {
      const d = err.response?.data?.detail;
      setError(typeof d === "string" ? d : "Login failed. Check credentials and try again.");
    } finally { setBusy(false); }
  };
  return <div className="login-shell">
    <form className="login-card" onSubmit={submit}>
      <div className="brand-mark login-mark">P</div>
      <h1>Paushtikk Switch</h1>
      <p>Sales intelligence workspace — please sign in.</p>
      <label>Email</label>
      <input data-testid="login-email" type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="admin@paushtikk.com" required autoFocus />
      <label>Password</label>
      <input data-testid="login-password" type="password" value={password} onChange={e => setPassword(e.target.value)} placeholder="••••••••" required />
      {error && <div data-testid="login-error" className="login-error">{error}</div>}
      <button data-testid="login-submit" className="button button-primary full" type="submit" disabled={busy}>{busy ? "Signing in…" : "Sign in"}</button>
    </form>
  </div>;
}

function Workspace({ authUser, onLogout }) {
  const [page, setPage] = useState("dashboard");
  const [stats, setStats] = useState(null);
  const [leads, setLeads] = useState([]);
  const [leadsPage, setLeadsPage] = useState(1);
  const [leadsMeta, setLeadsMeta] = useState({ total: 0, pages: 1, page: 1, page_size: 50 });
  const [followUps, setFollowUps] = useState({ total: 0, overdue: 0, due_today: 0, items: [], salespersons: [], assigned_to: "All", date: "", mode: "day" });
  const [followDate, setFollowDate] = useState("");
  const [followMode, setFollowMode] = useState("day");
  const [identity, setIdentity] = useState(readIdentity);
  const [integration, setIntegration] = useState({ shadow_mode: false, api_key_configured: false });
  const [bannerDismissed, setBannerDismissed] = useState(false);
  const [selected, setSelected] = useState(null);
  const [search, setSearch] = useState("");
  const [temperature, setTemperature] = useState("all");
  const [leadFilters, setLeadFilters] = useSessionFilters("leads-list-filters");
  // PRD 2026-03-10: user-selectable Lead Database sort — date_added (default, newest first) |
  // lead_score | last_contacted | sales_stage. Reset to page 1 whenever the sort changes.
  const [leadSort, setLeadSort] = useState("date_added");

  const scopedOwner = identity.isAdmin ? "All" : identity.name;
  const loadFollowUps = useCallback(async (owner = scopedOwner, date = followDate, mode = followMode) => {
    const params = {};
    if (owner && owner !== "All") params.assigned_to = owner;
    if (date) params.date = date;
    if (mode && mode !== "day") params.mode = mode;
    const r = await axios.get(`${API}/dashboard/follow-ups-today`, { params });
    setFollowUps(r.data);
  }, [scopedOwner, followDate, followMode]);
  const load = useCallback(async () => {
    const filterParams = toApiParams(leadFilters);
    const [s, l, ig] = await Promise.all([
      axios.get(`${API}/dashboard`),
      axios.get(`${API}/leads`, { params: { search, temperature, page: leadsPage, page_size: 50, sort: leadSort, ...filterParams } }),
      axios.get(`${API}/integration-status`),
    ]);
    const total = parseInt(l.headers["x-total-count"] || l.headers["X-Total-Count"] || "0", 10);
    const pages = parseInt(l.headers["x-total-pages"] || l.headers["X-Total-Pages"] || "1", 10);
    setStats(s.data); setLeads(l.data); setIntegration(ig.data);
    setLeadsMeta({ total, pages, page: leadsPage, page_size: 50 });
    // Atomic-move safety: mark-active / mark-wrong-classification / mark-lost hard-delete the lead
    // from `leads`. If the previously-selected lead is gone, clear the selection instead of silently
    // swapping to an unrelated lead — that way any modal guarded by `selected && ...` closes cleanly.
    setSelected(prev => prev ? (l.data.find(x => x.lead_id === prev.lead_id) || null) : (l.data[0] || null));
  }, [search, temperature, leadsPage, leadFilters, leadSort]);
  useEffect(() => { load().catch(console.error); }, [load]);
  useEffect(() => { loadFollowUps(scopedOwner, followDate, followMode).catch(console.error); }, [loadFollowUps, scopedOwner, followDate, followMode]);
  useEffect(() => { localStorage.setItem(IDENTITY_KEY, JSON.stringify(identity)); }, [identity]);
  useEffect(() => { setLeadsPage(1); }, [search, temperature, leadFilters, leadSort]);

  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    try { return localStorage.getItem("paushtikk.sidebar_collapsed") === "1"; }
    catch { return false; }
  });
  useEffect(() => {
    try { localStorage.setItem("paushtikk.sidebar_collapsed", sidebarCollapsed ? "1" : "0"); }
    catch { /* ignore quota */ }
  }, [sidebarCollapsed]);

  const openLead = (leadId) => {
    const target = leads.find(x => x.lead_id === leadId);
    if (target) { setSelected(target); setPage("leads"); }
    else {
      // Not in the currently loaded page — fetch it from the API and open Leads with it.
      axios.get(`${API}/leads/${leadId}`).then(r => { setSelected(r.data); setPage("leads"); }).catch(() => setPage("leads"));
    }
  };
  const markContacted = async (leadId) => {
    await axios.post(`${API}/leads/${leadId}/mark-contacted`);
    await Promise.all([loadFollowUps(scopedOwner), load()]);
  };

  const active = selected || leads[0];
  const showBanner = !bannerDismissed && followUps.total > 0 && page === "dashboard";
  const modeLabel = integration.shadow_mode ? "SHADOW MODE" : "LIVE MODE";
  const modeState = integration.shadow_mode ? "OFF" : "ON";

  return <div className="app-shell">
    <header className="topbar"><div className="brand"><div className="brand-mark">P</div><div><strong>Paushtikk <span>Switch</span></strong><small>SALES INTELLIGENCE</small></div></div><div className="top-actions"><div className="health"><i /> System healthy</div><div className={`shadow${integration.shadow_mode ? "" : " live"}`} data-testid="mode-indicator"><span>{modeLabel}</span><b data-testid="mode-state">{modeState}</b></div><NotificationBell onOpenLead={openLead} onGoto={setPage} /><button data-testid="logout-button" className="button button-ghost logout" onClick={onLogout}>{authUser.name.split(" ")[0]} · Sign out</button><div className="avatar">{(authUser.name || "AD").slice(0, 2).toUpperCase()}</div></div></header>
    <div className={`app-body${sidebarCollapsed ? " sidebar-collapsed" : ""}`}><aside className="sidebar" data-testid="sidebar">
      <button data-testid="sidebar-toggle" className="sidebar-toggle" onClick={() => setSidebarCollapsed(v => !v)} aria-label={sidebarCollapsed ? "Expand sidebar" : "Collapse sidebar"} title={sidebarCollapsed ? "Expand" : "Collapse"}>
        <ChevronRight size={14} className={`sidebar-toggle-icon${sidebarCollapsed ? "" : " rotated"}`} />
      </button>
      <div className="workspace-label">WORKSPACE</div>{nav.filter(n => !n.adminOnly || authUser.role === "ADMIN").map(({ id, label, icon: Icon }) => <button data-testid={`nav-${id}`} key={id} className={page === id ? "nav-item active" : "nav-item"} onClick={() => setPage(id)} title={sidebarCollapsed ? label : undefined}><Icon size={17} /><span className="nav-label">{label}</span>{id === "leads" && <em data-testid="nav-leads-count">{stats?.open_pipeline ?? 0}</em>}{id === "dashboard" && followUps.total > 0 && <em data-testid="nav-follow-up-badge" className="nav-pill">{followUps.total}</em>}</button>)}<div className="sidebar-bottom"><div className="workspace-label">OPERATIONS</div><button data-testid="nav-settings" className="nav-item" onClick={() => setPage("settings")} title={sidebarCollapsed ? "Settings" : undefined}><Settings2 size={17} /><span className="nav-label">Settings</span></button><IdentityPicker identity={identity} setIdentity={setIdentity} owners={followUps.salespersons || []} /></div></aside>
      <main className="main-content">
        <OnboardingWalkthrough onGoto={setPage} />
        {showBanner && <FollowUpBanner followUps={followUps} onDismiss={() => setBannerDismissed(true)} onJump={() => document.getElementById("followUpSection")?.scrollIntoView({ behavior: "smooth", block: "start" })} />}
        {page === "dashboard" && <Dashboard stats={stats} leads={leads} followUps={followUps} identity={identity} onSelect={(x) => { setSelected(x); setPage("leads"); }} onOpen={openLead} onMarkContacted={markContacted} onRefreshFollowUps={() => loadFollowUps(scopedOwner, followDate, followMode)} followDate={followDate} setFollowDate={setFollowDate} followMode={followMode} setFollowMode={setFollowMode} />}
        {page === "leads" && <LeadsPage leads={leads} selected={active} search={search} setSearch={setSearch} temperature={temperature} setTemperature={setTemperature} leadFilters={leadFilters} setLeadFilters={setLeadFilters} leadSort={leadSort} setLeadSort={setLeadSort} onSelect={setSelected} onRefresh={load} authUser={authUser} leadsMeta={leadsMeta} leadsPage={leadsPage} setLeadsPage={setLeadsPage} />}
        {/* Pipeline page removed per PRD 2026-03-07 — all lead tracking is done via Lead Database
            status filters. The Pipeline component definition stays in-file only as a dead route
            for defensive fallback; no nav entry references it. */}
        {page === "active" && <ActiveCustomers />}
        {page === "lost" && <LostLeads />}
        {page === "simulator" && <Simulator />}
        {page === "knowledge" && <KnowledgeBase />}
        {page === "webhooks" && <Webhooks />}
        {page === "team" && authUser.role === "ADMIN" && <Team currentUser={authUser} />}
        {page === "settings" && <Settings authUser={authUser} />}
      </main></div>
  </div>;
}

function NotificationBell({ onOpenLead, onGoto }) {
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/notifications`);
      setItems(data.notifications || []);
      setUnread(data.unread || 0);
    } catch (err) { /* silent — bell is best-effort */ }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, [load]);

  const markOne = async (nid) => {
    setBusy(true);
    try { await axios.post(`${API}/notifications/${nid}/mark-read`); await load(); }
    finally { setBusy(false); }
  };
  const markAll = async () => {
    setBusy(true);
    try { await axios.post(`${API}/notifications/mark-all-read`); await load(); }
    finally { setBusy(false); }
  };
  const openLead = (leadId) => { setOpen(false); onOpenLead?.(leadId); };

  return <div className="notif-bell" data-testid="notification-bell-wrapper">
    <button data-testid="notification-bell-button" className="notif-bell-btn" onClick={() => setOpen(v => !v)} aria-label="Notifications">
      <Bell size={16} />
      {unread > 0 && <em data-testid="notification-unread-badge" className="notif-badge">{unread > 99 ? "99+" : unread}</em>}
    </button>
    {open && <div className="notif-panel" data-testid="notification-panel">
      <div className="notif-panel-head">
        <strong>Notifications</strong>
        {items.length > 0 && <button data-testid="notification-mark-all-read" className="text-button" disabled={busy || unread === 0} onClick={markAll}>Mark all read</button>}
      </div>
      {items.length === 0 ? <div className="notif-empty" data-testid="notification-empty">You&apos;re all caught up.</div> : <ul className="notif-list">
        {items.map(n => <li key={n.notification_id} className={`notif-item${n.read ? "" : " unread"}`} data-testid={`notification-item-${n.notification_id}`}>
          <div className="notif-item-head">
            <b>{n.title}</b>
            <small>{relTime(n.created_at)}</small>
          </div>
          {(n.items || []).slice(0, 5).map(it => (
            <button key={it.lead_id} className="notif-lead-row" data-testid={`notification-lead-${it.lead_id}`} onClick={() => openLead(it.lead_id)}>
              <div>
                <strong>{it.customer_name}</strong>
                <span>{it.phone_number} · {it.sales_stage || "—"} · score {it.lead_score}{it.overdue ? " · OVERDUE" : ""}</span>
                {it.meal_summary && <em>{it.meal_summary}</em>}
              </div>
              <ChevronRight size={13} />
            </button>
          ))}
          {(n.items || []).length > 5 && <button className="text-button" onClick={() => { setOpen(false); onGoto?.("dashboard"); }}>View all {n.items.length} in dashboard →</button>}
          {!n.read && <div className="notif-actions"><button data-testid={`notification-mark-read-${n.notification_id}`} className="text-button" disabled={busy} onClick={() => markOne(n.notification_id)}>Mark read</button></div>}
        </li>)}
      </ul>}
    </div>}
  </div>;
}

function IdentityPicker({ identity, setIdentity, owners }) {
  const options = ["Admin", ...owners.filter(x => x && x !== "Admin")];
  const val = identity.isAdmin ? "Admin" : identity.name;
  const onChange = (e) => {
    const v = e.target.value;
    setIdentity(v === "Admin" ? { name: "Admin", isAdmin: true } : { name: v, isAdmin: false });
  };
  return <div className="profile identity-picker">
    <div className="profile-avatar">{(val || "AD").slice(0, 2).toUpperCase()}</div>
    <div className="identity-body">
      <small>SIGNED IN AS</small>
      <select data-testid="identity-picker" value={val} onChange={onChange}>
        {options.map(name => <option key={name} value={name}>{name === "Admin" ? "Admin — all leads" : name}</option>)}
      </select>
    </div>
  </div>;
}

function FollowUpBanner({ followUps, onDismiss, onJump }) {
  const { total, overdue, due_today, assigned_to } = followUps;
  const scope = assigned_to && assigned_to !== "All" ? ` · ${assigned_to}` : "";
  const detail = overdue > 0 ? `${overdue} overdue · ${due_today} due today${scope}` : `${due_today} due today${scope}`;
  return <div className="follow-up-banner" data-testid="follow-up-alert-banner" role="alert">
    <div className="fub-icon"><Bell size={16} /></div>
    <div className="fub-copy">
      <strong data-testid="follow-up-alert-count">{total} lead{total === 1 ? "" : "s"} need a follow-up</strong>
      <span data-testid="follow-up-alert-detail">{detail} · sorted by lead score</span>
    </div>
    <button data-testid="follow-up-alert-jump" className="button button-primary" onClick={onJump}>Review queue <ChevronRight size={14} /></button>
    <button data-testid="follow-up-alert-dismiss" className="icon-button fub-dismiss" onClick={onDismiss} aria-label="Dismiss follow-up alert"><X size={14} /></button>
  </div>;
}

function PageHeader({ eyebrow, title, subtitle, action }) { return <div className="page-header"><div><div className="eyebrow">{eyebrow}</div><h1 data-testid="page-header-title">{title}</h1><p>{subtitle}</p></div>{action}</div>; }

function Dashboard({ stats, leads, followUps, identity, onSelect, onOpen, onMarkContacted, onRefreshFollowUps, followDate, setFollowDate, followMode, setFollowMode }) {
  const cards = [
    { label: "Active leads", value: stats?.total || 0, trend: "+12.8%", icon: Users, tone: "blue" },
    { label: "Deals closed", value: stats?.deals_closed || 0, trend: `${stats?.conversion_rate || 0}% conv rate`, icon: ShieldCheck, tone: "green", testid: "kpi-deals-closed" },
    { label: "Total deal value", value: formatINRCompact(stats?.total_deal_value || 0), trend: "sum of ₹ closed", icon: Zap, tone: "teal", testid: "kpi-total-deal-value" },
    { label: "AI qualified rate", value: `${stats?.qualified_rate || 0}%`, trend: "+4.2%", icon: Bot, tone: "green" },
    { label: "Avg response time", value: stats?.avg_response || "—", trend: "−18s", icon: Zap, tone: "amber" },
    { label: "Hot leads", value: stats?.hot || 0, trend: "+3 today", icon: Flame, tone: "red" },
    { label: "Active skipped", value: stats?.active_skipped || 0, trend: "existing customers", icon: CircleHelp, tone: "purple" },
    { label: "Pipeline value", value: formatINRCompact(stats?.pipeline_value || 0), trend: "in-flight ₹", icon: ChevronRight, tone: "blue", testid: "kpi-pipeline-value" },
  ];
  // Time-based greeting driven by the browser's LOCAL time (not server time).
  // 5–12 → Morning · 12–17 → Afternoon · 17–21 → Evening · 21–5 → Night.
  const hour = new Date().getHours();
  const partOfDay = hour >= 5 && hour < 12 ? "Morning" : hour >= 12 && hour < 17 ? "Afternoon" : hour >= 17 && hour < 21 ? "Evening" : "Night";
  const firstName = identity.isAdmin ? "Admin" : identity.name.split(" ")[0];
  const greeting = `Good ${partOfDay}, ${firstName}`;
  const subtitle = identity.isAdmin ? "Here’s your lead intelligence at a glance." : `Here’s your personal queue and today’s activity, ${identity.name.split(" ")[0]}.`;
  return <><PageHeader eyebrow="MONDAY, 18 AUGUST 2025" title={greeting} subtitle={subtitle} action={<button data-testid="dashboard-refresh-button" className="button button-ghost" onClick={() => window.location.reload()}>Refresh data <Zap size={15} /></button>} />
    <div className="kpi-grid">{cards.map(({ label, value, trend, icon: Icon, tone, testid }) => <div data-testid={testid || `kpi-${label.toLowerCase().replaceAll(" ", "-")}`} className="kpi-card" key={label}><div className={`kpi-icon ${tone}`}><Icon size={18} /></div><span>{label}</span><strong>{value}</strong><small className="trend">{trend} <span>vs last week</span></small><div className={`spark spark-${tone}`} /></div>)}</div>
    <FollowUpSection followUps={followUps} identity={identity} onOpen={onOpen} onMarkContacted={onMarkContacted} onRefreshFollowUps={onRefreshFollowUps} followDate={followDate} setFollowDate={setFollowDate} followMode={followMode} setFollowMode={setFollowMode} />
    <LeadSourceBreakdown />
    {identity.isAdmin && <AgentPerformance />}
    <div className="dashboard-grid"><QualificationFunnel /><section className="panel activity-panel"><div className="panel-heading"><div><h2>Priority leads</h2><p>Needs attention today</p></div><button data-testid="view-all-leads-button" className="text-button" onClick={() => onSelect(leads[0])}>View all <ChevronRight size={14} /></button></div>{leads.slice(0, 4).map(lead => <button data-testid={`priority-lead-${lead.lead_id}`} className="priority-row" key={lead.lead_id} onClick={() => onSelect(lead)}><div className="lead-mini-avatar">{initials(lead.customer_name)}</div><div className="priority-copy"><strong>{lead.customer_name}</strong><span>{lead.intent} · {lead.location}</span></div><Badge tone={scoreTone(lead.lead_score)}>{lead.lead_temperature}</Badge><ChevronRight size={16} /></button>)}</section></div></>;
}

const FUNNEL_PRESETS = [
  { id: "today", label: "Today" },
  { id: "7d", label: "Last 7 days" },
  { id: "30d", label: "Last 30 days" },
  { id: "90d", label: "Last 90 days" },
  { id: "custom", label: "Custom" },
];

function QualificationFunnel() {
  const [period, setPeriod] = useState("30d");
  const [customFrom, setCustomFrom] = useState("");
  const [customTo, setCustomTo] = useState("");
  const [data, setData] = useState({ stages: [], total: 0 });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (period === "custom" && (!customFrom || !customTo)) return; // wait for both
    setLoading(true);
    setError("");
    const params = period === "custom"
      ? { period: "custom", date_from: customFrom, date_to: customTo }
      : { period };
    axios.get(`${API}/dashboard/funnel`, { params })
      .then(r => setData(r.data))
      .catch(err => setError(err.response?.data?.detail || "Could not load funnel data"))
      .finally(() => setLoading(false));
  }, [period, customFrom, customTo]);

  const maxCount = Math.max(1, ...data.stages.map(s => s.count));
  return <section className="panel funnel-panel">
    <div className="panel-heading">
      <div><h2>Qualification funnel</h2><p>Lead progression across the sales journey · {data.total} leads in window</p></div>
    </div>
    <div className="funnel-period-controls" data-testid="funnel-period-controls">
      {FUNNEL_PRESETS.map(p => (
        <button
          key={p.id}
          data-testid={`funnel-period-${p.id}`}
          className={period === p.id ? "period-chip active" : "period-chip"}
          onClick={() => setPeriod(p.id)}
        >{p.label}</button>
      ))}
    </div>
    {period === "custom" && <div className="funnel-custom-range" data-testid="funnel-custom-range">
      <label>From <input data-testid="funnel-custom-from" type="date" value={customFrom} onChange={e => setCustomFrom(e.target.value)} /></label>
      <label>To <input data-testid="funnel-custom-to" type="date" value={customTo} onChange={e => setCustomTo(e.target.value)} /></label>
      {(!customFrom || !customTo) && <small className="hint">Pick both dates to apply</small>}
    </div>}
    {error && <div className="funnel-error" data-testid="funnel-error">{error}</div>}
    <div className="funnel" data-testid="funnel-body">
      {loading ? <div className="funnel-loading">Loading…</div> : data.stages.map(({ stage, count }) => (
        <div className="funnel-row" key={stage} data-testid={`funnel-row-${stage.replaceAll(" ", "-").toLowerCase()}`}>
          <span>{stage}</span>
          <div className="funnel-track"><div style={{ width: `${Math.round((count / maxCount) * 100)}%` }} /></div>
          <b data-testid={`funnel-count-${stage.replaceAll(" ", "-").toLowerCase()}`}>{count}</b>
        </div>
      ))}
    </div>
  </section>;
}

/**
 * Inline "edit follow-up date" pill for each Follow-Up row in the Overview page.
 * Per PRD 2026-03-08: agents can bump a follow-up date without opening the Lead Database.
 * Click the pencil to reveal a native date picker; picking + Save PATCHes /api/leads/{id}.
 */
function FollowUpDateEditor({ leadId, currentIso, onSaved }) {
  const currentISODate = currentIso ? new Date(currentIso).toISOString().slice(0, 10) : "";
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(currentISODate);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  useEffect(() => { setValue(currentISODate); }, [currentISODate]);
  const save = async () => {
    setBusy(true); setErr("");
    try {
      await axios.patch(`${API}/leads/${leadId}`, {
        next_follow_up_at: value ? `${value}T10:00:00+00:00` : null,
      });
      setEditing(false);
      if (onSaved) await onSaved();
    } catch (e) {
      setErr(e.response?.data?.detail || "Could not update follow-up date");
    } finally { setBusy(false); }
  };
  const cancel = () => { setValue(currentISODate); setEditing(false); setErr(""); };
  if (!editing) {
    return (
      <span className="follow-up-next-inline" data-testid={`follow-up-next-${leadId}`}>
        Follow-up: {currentIso ? new Date(currentIso).toLocaleDateString() : "—"}
        <button
          type="button"
          data-testid={`follow-up-edit-${leadId}`}
          className="follow-up-edit-btn"
          onClick={(e) => { e.stopPropagation(); setEditing(true); }}
          title="Edit follow-up date"
        ><Clock size={11} /> Edit</button>
      </span>
    );
  }
  return (
    <span className="follow-up-next-inline follow-up-editing" data-testid={`follow-up-editor-${leadId}`}>
      <input
        type="date"
        data-testid={`follow-up-date-input-${leadId}`}
        className="follow-up-date-input"
        value={value}
        onChange={e => setValue(e.target.value)}
        onClick={e => e.stopPropagation()}
        autoFocus
      />
      <button
        type="button"
        data-testid={`follow-up-save-${leadId}`}
        className="button button-primary follow-up-save-btn"
        disabled={busy}
        onClick={(e) => { e.stopPropagation(); save(); }}
      >{busy ? "Saving…" : "Save"}</button>
      <button
        type="button"
        data-testid={`follow-up-cancel-${leadId}`}
        className="button follow-up-cancel-btn"
        disabled={busy}
        onClick={(e) => { e.stopPropagation(); cancel(); }}
      >Cancel</button>
      {err && <em className="follow-up-edit-error" data-testid={`follow-up-edit-error-${leadId}`}>{err}</em>}
    </span>
  );
}


function FollowUpSection({ followUps, identity, onOpen, onMarkContacted, onRefreshFollowUps, followDate, setFollowDate, followMode, setFollowMode }) {
  const items = followUps.items || [];
  const [busyId, setBusyId] = useState(null);
  const [remindBusy, setRemindBusy] = useState(false);
  const [remindResult, setRemindResult] = useState(null);
  const handleMark = async (id) => { setBusyId(id); try { await onMarkContacted(id); } finally { setBusyId(null); } };
  const handleSendReminders = async () => {
    setRemindBusy(true); setRemindResult(null);
    try {
      const params = followDate ? { date: followDate } : {};
      const { data } = await axios.post(`${API}/dashboard/follow-ups/send-reminders`, null, { params });
      setRemindResult(data);
    } catch (err) {
      setRemindResult({ error: err.response?.data?.detail || "Could not send reminders" });
    } finally { setRemindBusy(false); }
  };
  const label = useMemo(() => followUps.overdue > 0 ? `${followUps.overdue} overdue · ${followUps.due_today} due today` : `${followUps.due_today} due today`, [followUps]);
  const scopedTo = identity.isAdmin ? null : identity.name;
  const emptyLine = scopedTo ? `${scopedTo} is caught up — no follow-ups queued.` : "No follow-ups on this day — clear queue.";
  const todayISO = new Date().toISOString().slice(0, 10);
  const selectedDate = followUps.date || followDate || todayISO;
  const isUpcoming = followMode === "upcoming";
  const isTodaySelected = !isUpcoming && (!followDate || followDate === todayISO);
  const headerText = isUpcoming ? "Upcoming follow-ups" : (isTodaySelected ? "Follow up today" : `Follow-ups on ${selectedDate}`);
  return <section id="followUpSection" className="panel follow-up-panel" data-testid="follow-up-today-section">
    <div className="panel-heading">
      <div>
        <h2><Clock size={16} /> {headerText}</h2>
        <p data-testid="follow-up-summary">{items.length === 0 ? emptyLine : `${isUpcoming ? `${followUps.total} scheduled ahead` : label} · ${isUpcoming ? "sorted by next follow-up date" : "sorted by highest lead score first"}${scopedTo ? ` · showing ${scopedTo}'s queue` : ""}`}</p>
      </div>
      <div className="follow-up-controls">
        <div className="fu-date-picker">
          <label htmlFor="fu-mode-select" className="fu-scope-label">VIEW</label>
          <select id="fu-mode-select" data-testid="follow-up-mode-select" value={followMode} onChange={e => setFollowMode(e.target.value)}>
            <option value="day">Specific day</option>
            <option value="upcoming">Upcoming</option>
          </select>
          <label htmlFor="fu-date-input" className="fu-scope-label">{isUpcoming ? "FROM" : "DATE"}</label>
          <input id="fu-date-input" data-testid="follow-up-date-picker" type="date" value={followDate || todayISO} onChange={e => setFollowDate(e.target.value)} />
          {(followDate && followDate !== todayISO) && <button data-testid="follow-up-date-reset" className="fu-date-reset" onClick={() => { setFollowDate(""); setFollowMode("day"); }}>Today</button>}
        </div>
        {scopedTo ? <Badge tone="blue" data-testid="follow-up-scope-badge">MY QUEUE</Badge> : <Badge tone="purple" data-testid="follow-up-scope-badge">ALL LEADS</Badge>}
        <Badge tone={followUps.overdue > 0 ? "red" : "green"}>{followUps.total} in queue</Badge>
        {identity.isAdmin && items.length > 0 && <button data-testid="follow-up-send-reminders" className="button button-primary" disabled={remindBusy} onClick={handleSendReminders} title="Creates an in-app notification for each assigned salesperson. Agents do NOT receive WhatsApp."><Bell size={13} /> {remindBusy ? "Notifying…" : "Notify agents (in-app)"}</button>}
      </div>
    </div>
    {remindResult && <div className={`reminder-notice${remindResult.error ? " error" : (remindResult.delivered === 0 ? " warning" : "")}`} data-testid="follow-up-reminder-notice">
      {remindResult.error && <span>{remindResult.error}</span>}
      {!remindResult.error && remindResult.delivered === 0 && <span data-testid="follow-up-reminder-empty">No matching agent accounts — reminders were not delivered. Ask your admin to add a user whose name matches the salespeople assigned in the queue.</span>}
      {!remindResult.error && remindResult.delivered > 0 && <>
        <b>{remindResult.delivered}/{remindResult.total_salespeople}</b> salespeople notified in-app · agents will see their reminder in the notification bell (no WhatsApp sent to agents)
        {remindResult.results?.some(r => !r.delivered) && <ul>
          {remindResult.results.filter(r => !r.delivered).map(r => <li key={r.salesperson}>{r.salesperson}: {r.reason || "not delivered"}</li>)}
        </ul>}
      </>}
    </div>}
    {items.length === 0 ? (
      <div className="empty-state" data-testid="follow-up-empty"><Clock size={30} /><strong>Nothing pending</strong><span>{scopedTo ? "Switch to Admin from the sidebar to see all leads." : "Mark a lead as contacted or set a follow-up to see it here."}</span></div>
    ) : (
      <ul className="follow-up-list">
        {items.map((item, idx) => (
          <li key={item.lead_id} className={`follow-up-row${item.overdue ? " overdue" : ""}`} data-testid={`follow-up-item-${item.lead_id}`}>
            <div className="fur-rank">#{idx + 1}</div>
            <div className="fur-avatar">{initials(item.customer_name)}</div>
            <div className="fur-body">
              <div className="fur-top">
                <strong data-testid={`follow-up-name-${item.lead_id}`}>{item.customer_name}</strong>
                <span className="fur-phone" data-testid={`follow-up-phone-${item.lead_id}`}><Phone size={11} /> {item.phone_number}</span>
                {item.overdue && <Badge tone="red">OVERDUE</Badge>}
                {item.sunday_serviceable === false && <Badge tone="amber" data-testid={`follow-up-sunday-flag-${item.lead_id}`}>NO SUNDAY</Badge>}
              </div>
              <p className="fur-summary" data-testid={`follow-up-summary-${item.lead_id}`}>{item.meal_summary}</p>
              <div className="fur-meta">
                <span data-testid={`follow-up-last-contact-${item.lead_id}`}>Last contact: {relTime(item.last_contact_at)}</span>
                <span className="dot">·</span>
                <FollowUpDateEditor
                  leadId={item.lead_id}
                  currentIso={item.next_follow_up_at}
                  onSaved={onRefreshFollowUps}
                />
                <span className="dot">·</span>
                <span>Owner: {item.assigned_salesperson}</span>
              </div>
            </div>
            <div className="fur-score">
              <b data-testid={`follow-up-score-${item.lead_id}`}>{item.lead_score}</b>
              <Badge tone={scoreTone(item.lead_score)}>{item.lead_temperature}</Badge>
            </div>
            <div className="fur-actions">
              <button data-testid={`follow-up-open-${item.lead_id}`} className="button button-primary" onClick={() => onOpen(item.lead_id)}>Open <ChevronRight size={13} /></button>
              <a data-testid={`follow-up-nudge-${item.lead_id}`} href={nudgeUrl(item)} target="_blank" rel="noopener noreferrer" className="button button-whatsapp" title={nudgeMessage(item)}><MessageCircle size={13} /> Nudge</a>
              <button data-testid={`follow-up-mark-${item.lead_id}`} className="button" disabled={busyId === item.lead_id} onClick={() => handleMark(item.lead_id)}><PhoneCall size={13} /> {busyId === item.lead_id ? "Saving…" : "Mark contacted"}</button>
            </div>
          </li>
        ))}
      </ul>
    )}
  </section>;
}


function LeadSourceBreakdown() {
  const [data, setData] = useState({ items: [], totals: {} });
  const [busy, setBusy] = useState(true);
  const [error, setError] = useState("");
  useEffect(() => {
    axios.get(`${API}/dashboard/lead-sources`).then(r => { setData(r.data); setBusy(false); }).catch(e => { setError(e.response?.data?.detail || "Could not load lead sources"); setBusy(false); });
  }, []);
  const totalLeads = data.totals?.leads || 0;
  return <section className="panel source-panel" data-testid="lead-source-panel">
    <div className="panel-heading"><div><h2><PieChart size={16} /> Lead source breakdown</h2><p>Which channels are driving new conversations. Closed = paid or active customers.</p></div><Badge tone="blue" data-testid="lead-source-total">{totalLeads} leads</Badge></div>
    {busy && <div className="empty-state" data-testid="source-loading"><PieChart size={26} /><strong>Loading…</strong></div>}
    {error && !busy && <div className="pipeline-error" data-testid="source-error">{error}</div>}
    {!busy && !error && data.items.length === 0 && <div className="empty-state" data-testid="source-empty"><PieChart size={26} /><strong>No leads yet</strong><span>Once leads arrive they&apos;ll be grouped by source here.</span></div>}
    {!busy && !error && data.items.length > 0 && <div className="source-list">{data.items.map(item => (
      <div key={item.source} className="source-row" data-testid={`source-row-${item.source.replaceAll(" ", "-").replaceAll("/", "-")}`}>
        <div className="source-label"><strong>{item.source}</strong><span>{item.count} leads · {item.closed} closed · {item.conversion_rate}% conv</span></div>
        <div className="source-bar-wrap"><div className="source-bar" style={{ width: `${Math.max(3, item.share_pct)}%` }} /></div>
        <div className="source-share"><b data-testid={`source-share-${item.source.replaceAll(" ", "-").replaceAll("/", "-")}`}>{item.share_pct}%</b></div>
      </div>
    ))}</div>}
  </section>;
}


function AgentPerformance() {
  const [data, setData] = useState({ items: [], totals: {} });
  const [busy, setBusy] = useState(true);
  const [error, setError] = useState("");
  const [selectedAgents, setSelectedAgents] = useState([]);
  const [search, setSearch] = useState("");
  const load = useCallback(async () => {
    setBusy(true); setError("");
    try { const r = await axios.get(`${API}/dashboard/agent-performance`); setData(r.data); }
    catch (err) { setError(err.response?.data?.detail || "Could not load agent performance"); }
    finally { setBusy(false); }
  }, []);
  useEffect(() => { load(); }, [load]);
  const rows = useMemo(() => {
    const items = data.items || [];
    const filtered = search ? items.filter(x => x.agent.toLowerCase().includes(search.toLowerCase())) : items;
    return selectedAgents.length ? filtered.filter(x => selectedAgents.includes(x.agent)) : filtered;
  }, [data, search, selectedAgents]);
  const toggleAgent = (name) => setSelectedAgents(prev => prev.includes(name) ? prev.filter(x => x !== name) : [...prev, name]);
  const fmtINR = formatINR;  // shared helper — Indian digit grouping
  const fmtDate = (iso) => iso ? new Date(iso).toLocaleDateString("en-IN", { day: "2-digit", month: "short" }) : "—";
  return <section className="panel agent-perf-panel" data-testid="agent-performance-panel">
    <div className="panel-heading">
      <div>
        <h2><TrendingUp size={16} /> Agent performance</h2>
        <p>Leads closed, deal value, conversion, and last activity for each sales agent.</p>
      </div>
      <div className="agent-perf-controls">
        <input data-testid="agent-search" placeholder="Filter agents…" value={search} onChange={e => setSearch(e.target.value)} />
        {selectedAgents.length > 0 && <button data-testid="agent-clear-compare" className="button button-ghost" onClick={() => setSelectedAgents([])}>Clear ({selectedAgents.length})</button>}
      </div>
    </div>
    {busy && <div className="empty-state" data-testid="agent-loading"><TrendingUp size={26} /><strong>Loading team metrics…</strong></div>}
    {error && !busy && <div className="pipeline-error" data-testid="agent-error">{error}</div>}
    {!busy && !error && rows.length === 0 && <div className="empty-state" data-testid="agent-empty"><TrendingUp size={26} /><strong>No agent data yet</strong><span>Assign leads to salespeople to see performance rollups here.</span></div>}
    {!busy && !error && rows.length > 0 && <>
      <div className="agent-summary">
        <div><small>AGENTS</small><b data-testid="agent-total-count">{data.totals?.agents || 0}</b></div>
        <div><small>LEADS CLOSED</small><b data-testid="agent-total-closed">{data.totals?.leads_closed || 0}</b></div>
        <div><small>DEAL VALUE</small><b data-testid="agent-total-value">{fmtINR(data.totals?.deal_value_total)}</b></div>
        <div><small>AVG CONVERSION</small><b data-testid="agent-total-conv">{data.totals?.avg_conversion_rate || 0}%</b></div>
      </div>
      <div className="table-wrap"><table><thead><tr><th className="col-check"></th><th>Agent</th><th>Leads closed</th><th>Total deal value</th><th>Recent payments</th><th>Conversion</th><th>Last activity</th></tr></thead>
        <tbody>{rows.map(row => (
          <tr key={row.agent} data-testid={`agent-row-${row.agent.replaceAll(" ", "-")}`}>
            <td className="col-check" onClick={e => e.stopPropagation()}><input data-testid={`agent-compare-${row.agent.replaceAll(" ", "-")}`} type="checkbox" checked={selectedAgents.includes(row.agent)} onChange={() => toggleAgent(row.agent)} /></td>
            <td><div className="table-lead"><div className="lead-mini-avatar">{initials(row.agent)}</div><div><strong>{row.agent}</strong><span>{row.total_leads} lead{row.total_leads === 1 ? "" : "s"} · {row.hot} hot / {row.warm} warm</span></div></div></td>
            <td><div className="score"><b data-testid={`agent-closed-${row.agent.replaceAll(" ", "-")}`}>{row.leads_closed}</b>{row.active_customers > 0 && <Badge tone="green">{row.active_customers} ACTIVE</Badge>}</div></td>
            <td><b data-testid={`agent-value-${row.agent.replaceAll(" ", "-")}`}>{fmtINR(row.deal_value_total)}</b>{row.avg_deal_value > 0 && <small>avg {fmtINR(row.avg_deal_value)}</small>}</td>
            <td>{row.payment_dates.length === 0 ? <span className="muted">No payments yet</span> :
              <ul className="agent-payments">{row.payment_dates.map(p => <li key={p.lead_id + p.payment_date}><b>{fmtDate(p.payment_date)}</b> · {p.customer_name} · {fmtINR(p.amount)}</li>)}</ul>}
            </td>
            <td><Badge tone={conversionTone(row.conversion_rate)} data-testid={`agent-conv-${row.agent.replaceAll(" ", "-")}`}>{row.conversion_rate}%</Badge></td>
            <td data-testid={`agent-last-${row.agent.replaceAll(" ", "-")}`}>{row.last_activity_at ? relTime(row.last_activity_at) : "—"}</td>
          </tr>
        ))}</tbody>
      </table></div>
    </>}
  </section>;
}



function LeadsPage({ leads, selected, search, setSearch, temperature, setTemperature, leadFilters, setLeadFilters, leadSort, setLeadSort, onSelect, onRefresh, authUser, leadsMeta, leadsPage, setLeadsPage }) {
  const [checked, setChecked] = useState(() => new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  const [assignTo, setAssignTo] = useState("");
  const [bulkStage, setBulkStage] = useState("");
  const [showBulkConvert, setShowBulkConvert] = useState(false);
  const [bulkActiveMenuOpen, setBulkActiveMenuOpen] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showAddLead, setShowAddLead] = useState(false);
  const [agents, setAgents] = useState([]);
  const [sourceOptions, setSourceOptions] = useState([]);
  const stageOptions = ["NEW LEAD", "AI QUALIFYING", "QUALIFIED", "PAYMENT PENDING", "HUMAN HANDOVER", "CONVERTED", "NURTURE", "LOST"];
  useEffect(() => {
    axios.get(`${API}/team/agents`).then(r => setAgents([...new Set(Array.isArray(r.data) ? r.data : (r.data?.agents || []))])).catch(() => setAgents([]));
    axios.get(`${API}/dashboard/lead-source-options`).then(r => setSourceOptions(r.data.lead_sources || [])).catch(() => setSourceOptions([]));
  }, []);
  const toggle = (id, e) => {
    e.stopPropagation();
    setChecked(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  };
  const toggleAll = () => setChecked(prev => prev.size === leads.length ? new Set() : new Set(leads.map(l => l.lead_id)));
  const runBulk = async (action, extra = {}) => {
    if (checked.size === 0) return;
    if (action === "delete" && !window.confirm(`Delete ${checked.size} leads? This cannot be undone.`)) return;
    setBulkBusy(true);
    try { await axios.post(`${API}/leads/bulk`, { lead_ids: Array.from(checked), action, ...extra }); setChecked(new Set()); await onRefresh(); }
    finally { setBulkBusy(false); }
  };
  const runBulkConvert = async (rows) => {
    if (!_bulkSourceOK()) return;
    setBulkBusy(true);
    try {
      await axios.post(`${API}/leads/bulk-convert`, { conversions: rows });
      setShowBulkConvert(false);
      setChecked(new Set());
      await onRefresh();
    } finally { setBulkBusy(false); }
  };
  const runBulkWrongClassification = async () => {
    if (checked.size === 0) return;
    // PRD 2026-03-09 (revised): Wrong Classification is EXEMPT from the Lead Source gate.
    if (!window.confirm(`Reclassify ${checked.size} lead${checked.size === 1 ? "" : "s"} as active customers?\n\nThe classifier will learn from these messages so similar ones route to Active Customers automatically in future. This does NOT count as a sale.`)) return;
    setBulkBusy(true); setBulkActiveMenuOpen(false);
    try {
      await axios.post(`${API}/leads/bulk-wrong-classification`, { lead_ids: Array.from(checked) });
      setChecked(new Set());
      await onRefresh();
    } finally { setBulkBusy(false); }
  };
  const exportXlsx = async () => {
    const r = await axios.get(`${API}/leads-export.xlsx`, { params: { search, temperature }, responseType: "blob" });
    const url = URL.createObjectURL(new Blob([r.data]));
    const a = document.createElement("a");
    a.href = url; a.download = `paushtikk-leads-${new Date().toISOString().slice(0, 10)}.xlsx`;
    document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
  };
  const openLeadModal = (lead) => { onSelect(lead); setShowDetailModal(true); };
  // PRD 2026-03-09: expose a global hook so the AddLeadManuallyModal (or any other modal
  // outside this component tree) can open the detail view for the duplicate contact it
  // just discovered. Bare-bones bridge — no context/prop drilling needed for a single
  // handoff point.
  useEffect(() => {
    window.__openLeadDetail = (lead) => { if (lead) { onSelect(lead); setShowDetailModal(true); } };
    return () => { if (window.__openLeadDetail) delete window.__openLeadDetail; };
  }, [onSelect]);
  const closeLeadModal = () => setShowDetailModal(false);
  const handleBulkStageChange = (value) => {
    setBulkStage(value);
    if (!value) return;
    if (value === "CONVERTED") {
      setShowBulkConvert(true);
      setBulkStage("");
      return;
    }
    runBulk("change_stage", { stage: value }).then(() => setBulkStage(""));
  };
  const canDelete = authUser?.role === "ADMIN";
  const canManageActive = authUser?.role === "ADMIN" || authUser?.role === "SALES_MANAGER" || authUser?.role === "SALES_EXECUTIVE";
  const selectedLeads = leads.filter(l => checked.has(l.lead_id));
  // PRD 2026-03-09: every state-change bulk action must have every selected lead carry a
  // lead_source. If any row is missing one, block the action and show an inline error naming
  // the offenders so the user can fix them via the per-row inline picker.
  const [bulkSourceError, setBulkSourceError] = useState("");
  useEffect(() => { setBulkSourceError(""); }, [checked, leads.length]);
  const _bulkSourceOK = () => {
    const missing = selectedLeads.filter(l => !(l.lead_source && String(l.lead_source).trim()));
    if (missing.length) {
      const preview = missing.slice(0, 3).map(l => l.customer_name || l.lead_id).join(", ");
      const suffix = missing.length > 3 ? ` (+${missing.length - 3} more)` : "";
      setBulkSourceError(`Please select a Lead Source before continuing. Missing source on: ${preview}${suffix}`);
      return false;
    }
    setBulkSourceError("");
    return true;
  };
  // PRD 2026-03-09 (bug fix): inline PATCH — every inline dropdown/date input in the
  // Lead Database uses this. Errors are surfaced to the row-level `patchError` state so
  // the user knows a save didn't stick, and `onRefresh()` runs in `finally` so the row
  // always reconciles with the server (successful save → new value; failure → revert).
  const [patchError, setPatchError] = useState("");
  const patchLead = async (lead_id, body) => {
    setPatchError("");
    try {
      await axios.patch(`${API}/leads/${lead_id}`, body);
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || "Could not save that change";
      console.error("inline patch failed", e);
      setPatchError(typeof msg === "string" ? msg : JSON.stringify(msg));
    } finally {
      try { await onRefresh(); } catch { /* refresh failure is non-fatal here */ }
    }
  };
  return <><PageHeader eyebrow="LEAD OPERATIONS / 01" title="Leads database" subtitle="Every conversation, qualified and routed with context." action={<div className="page-header-actions"><button data-testid="add-lead-manually-button" className="button button-primary" onClick={() => setShowAddLead(true)}><UserPlus size={15} /> Add lead manually</button><button data-testid="export-leads-button" className="button button-ghost" onClick={exportXlsx}><Zap size={15} /> Export</button></div>} />
    {checked.size > 0 && <div className="bulk-toolbar" data-testid="bulk-toolbar">
      <span data-testid="bulk-count"><b>{checked.size}</b> selected</span>
      <button data-testid="bulk-mark-contacted" className="button" disabled={bulkBusy} onClick={() => runBulk("mark_contacted")}><PhoneCall size={13} /> Mark contacted</button>
      {canManageActive && <div className="split-button" data-testid="bulk-mark-active-split">
        <button data-testid="bulk-mark-active" className="button button-success" disabled={bulkBusy} onClick={() => setBulkActiveMenuOpen(v => !v)}><ShieldCheck size={13} /> {bulkBusy ? "Working…" : `Convert to active (${checked.size})`} <ChevronRight size={11} className={`split-caret${bulkActiveMenuOpen ? " open" : ""}`} /></button>
        {bulkActiveMenuOpen && <div className="split-menu" data-testid="bulk-mark-active-menu" onClick={e => e.stopPropagation()}>
          <button data-testid="bulk-mark-active-option-convert" className="split-menu-item" onClick={() => { if (!_bulkSourceOK()) return; setBulkActiveMenuOpen(false); setShowBulkConvert(true); }}>
            <strong>Convert to Active Customers</strong>
            <small>Intentional conversion — fill deal value + closed by + notes per customer</small>
          </button>
          <button data-testid="bulk-mark-active-option-wrong" className="split-menu-item danger" disabled={bulkBusy} onClick={runBulkWrongClassification}>
            <strong>Wrong Classification</strong>
            <small>Skip forms — {checked.size} lead{checked.size === 1 ? "" : "s"} were misclassified, use their messages as training data</small>
          </button>
        </div>}
      </div>}
      <select data-testid="bulk-stage-select" value={bulkStage} onChange={e => handleBulkStageChange(e.target.value)}><option value="">Change stage…</option>{stageOptions.map(s => <option key={s} value={s}>{s}</option>)}</select>
      <input data-testid="bulk-assign-input" placeholder="Assign to…" value={assignTo} onChange={e => setAssignTo(e.target.value)} onKeyDown={e => { if (e.key === "Enter" && assignTo.trim()) { runBulk("assign", { assigned_salesperson: assignTo.trim() }).then(() => setAssignTo("")); } }} />
      {canDelete && <button data-testid="bulk-delete" className="button button-danger" disabled={bulkBusy} onClick={() => runBulk("delete")}><Trash2 size={13} /> Delete</button>}
      <button data-testid="bulk-clear" className="button button-ghost" onClick={() => setChecked(new Set())}>Clear</button>
    </div>}
    {bulkSourceError && <div className="lead-source-gate-error" data-testid="bulk-source-gate-error" role="alert">{bulkSourceError}</div>}
    {patchError && <div className="lead-source-gate-error" data-testid="inline-patch-error" role="alert" onClick={() => setPatchError("")}>{patchError} <em style={{opacity:0.7,marginLeft:8}}>(click to dismiss)</em></div>}
    <section className="panel table-panel leads-full-width" data-testid="leads-database-panel">
      <ListFilters filters={leadFilters} setFilters={setLeadFilters} agents={[...new Set(leads.map(l => l.assigned_salesperson).filter(Boolean))]} testIdPrefix="leads-filter" />
      <div className="toolbar">
        <div className="search-box"><Search size={16} /><input data-testid="lead-search-input" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search name or phone..." /></div>
        <select data-testid="temperature-filter" value={temperature} onChange={e => setTemperature(e.target.value)}><option value="all">All temperatures</option><option value="HOT">Hot</option><option value="WARM">Warm</option><option value="COLD">Cold</option></select>
        <select data-testid="lead-sort-select" className="sort-select" value={leadSort} onChange={e => setLeadSort(e.target.value)} title="Sort the lead list">
          <option value="date_added">Date added · newest first</option>
          <option value="lead_score">Lead score · highest first</option>
          <option value="last_contacted">Last contacted · most recent first</option>
          <option value="sales_stage">Sales stage · grouped A–Z</option>
        </select>
        <button data-testid="lead-filter-button" className="icon-button"><ListFilter size={16} /></button>
      </div>
      {/* Wide table with horizontal scroll — 6 columns per PRD 2026-03-06. Underlying data
          (lost_reason, payment_date, sales_stage) is still kept in the DB and editable via the
          Edit Lead modal — only hidden from the row view. */}
      <div className="followup-legend" data-testid="followup-legend">
        <span><span className="dot overdue" /> Overdue</span>
        <span><span className="dot today" /> Today</span>
        <span><span className="dot upcoming" /> Upcoming</span>
      </div>
      <div className="table-wrap leads-wide-table"><table className="leads-table-expanded"><thead><tr>
        <th className="col-check"><input data-testid="bulk-select-all" type="checkbox" checked={checked.size > 0 && checked.size === leads.length} onChange={toggleAll} /></th>
        <th>Name</th>
        <th>Phone</th>
        <th>Assigned to</th>
        <th>Lead source</th>
        <th>Lead creation date</th>
        <th title="Number of times the follow-up date has been changed for this lead">Follow-up count</th>
        <th>Follow-up date</th>
        <th title="Optional time on the follow-up date. When set, the assigned salesperson gets an in-app notification 15 minutes before this time.">Reminder time</th>
      </tr></thead><tbody>{leads.map(lead => {
        const isLost = lead.sales_stage === "LOST";
        const isConverted = lead.converted || lead.sales_stage === "CONVERTED";
        const followUpVal = lead.next_follow_up_at ? new Date(lead.next_follow_up_at).toISOString().slice(0, 10) : "";
        // PRD 2026-03-10: reminder time — parsed out of `follow_up_reminder_at` (ISO datetime).
        // Displayed as HH:mm in the user's local timezone so agents see the wall-clock time.
        const reminderVal = lead.follow_up_reminder_at ? (() => {
          const d = new Date(lead.follow_up_reminder_at);
          const hh = String(d.getHours()).padStart(2, "0");
          const mm = String(d.getMinutes()).padStart(2, "0");
          return `${hh}:${mm}`;
        })() : "";
        const createdAtDisplay = lead.created_at ? new Date(lead.created_at).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "2-digit" }) : "—";
        // PRD 2026-03-09: colour-code the row by follow-up urgency so agents can eyeball the
        // list. Red = overdue (past date), yellow = due today, green = upcoming (future).
        // Converted/lost rows keep their own visual state and skip the urgency stripe.
        const followUpDay = followUpVal;
        const todayStr = new Date().toISOString().slice(0, 10);
        let urgencyClass = "";
        if (!isConverted && !isLost && followUpDay) {
          if (followUpDay < todayStr) urgencyClass = " row-followup-overdue";
          else if (followUpDay === todayStr) urgencyClass = " row-followup-today";
          else urgencyClass = " row-followup-upcoming";
        }
        return <tr data-testid={`lead-row-${lead.lead_id}`} className={`${isConverted ? "row-converted" : ""}${isLost ? " row-lost" : ""}${urgencyClass}`} key={lead.lead_id} onClick={() => openLeadModal(lead)}>
          <td className="col-check" onClick={e => e.stopPropagation()}><input data-testid={`lead-check-${lead.lead_id}`} type="checkbox" checked={checked.has(lead.lead_id)} onChange={e => toggle(lead.lead_id, e)} /></td>
          <td><div className="table-lead"><div className="lead-mini-avatar">{initials(lead.customer_name)}</div><div><strong>{lead.customer_name}</strong>{isConverted && <em className="row-converted-badge" data-testid={`lead-converted-badge-${lead.lead_id}`}>CONVERTED</em>}{isLost && <em className="row-lost-badge" data-testid={`lead-lost-badge-${lead.lead_id}`}>LOST</em>}</div></div></td>
          <td className="col-phone" data-testid={`lead-phone-${lead.lead_id}`}>{lead.phone_number}</td>
          <td onClick={e => e.stopPropagation()}>
            <select data-testid={`inline-owner-${lead.lead_id}`} className="inline-select" value={lead.assigned_salesperson || "Unassigned"} onChange={e => patchLead(lead.lead_id, { assigned_salesperson: e.target.value })}>
              <option value="Unassigned">Unassigned</option>
              {agents.map(a => <option key={a} value={a}>{a}</option>)}
            </select>
          </td>
          <td onClick={e => e.stopPropagation()}>
            <select data-testid={`inline-source-${lead.lead_id}`} className="inline-select" value={lead.lead_source || ""} onChange={e => patchLead(lead.lead_id, { lead_source: e.target.value || null })}>
              <option value="">— Not set —</option>
              {sourceOptions.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </td>
          <td className="col-created-at" data-testid={`lead-created-at-${lead.lead_id}`} title={lead.created_at || ""}>{createdAtDisplay}</td>
          <td className="col-followup-count" data-testid={`lead-followup-count-${lead.lead_id}`}>{lead.follow_up_count ?? 0}</td>
          <td onClick={e => e.stopPropagation()}>
            <input data-testid={`inline-followup-${lead.lead_id}`} className="inline-date" type="date" value={followUpVal} onChange={e => patchLead(lead.lead_id, { next_follow_up_at: e.target.value ? `${e.target.value}T10:00:00+00:00` : null })} />
          </td>
          <td onClick={e => e.stopPropagation()}>
            <input data-testid={`inline-reminder-${lead.lead_id}`} className="inline-date" type="time" value={reminderVal} disabled={!followUpVal} title={followUpVal ? "Reminder fires 15 min before this time on the follow-up date" : "Set a follow-up date first"} onChange={e => {
              const t = e.target.value;
              if (!followUpVal) return;
              // Combine date + time in LOCAL timezone and send as ISO (with offset).
              const iso = t ? new Date(`${followUpVal}T${t}:00`).toISOString() : null;
              patchLead(lead.lead_id, { follow_up_reminder_at: iso });
            }} />
          </td>
        </tr>;
      })}</tbody></table></div>
      <Pagination page={leadsPage} pages={leadsMeta.pages} total={leadsMeta.total} pageSize={leadsMeta.page_size} onPage={setLeadsPage} />
    </section>
    {showDetailModal && selected && <LeadDetailModal lead={selected} authUser={authUser} onClose={closeLeadModal} onRefresh={onRefresh} />}
    {showBulkConvert && <BulkConvertModal leads={selectedLeads} agents={agents} busy={bulkBusy} onCancel={() => setShowBulkConvert(false)} onSubmit={runBulkConvert} />}
    {showAddLead && <AddLeadManuallyModal onClose={() => setShowAddLead(false)} onSaved={onRefresh} />}
  </>;
}

function Pagination({ page, pages, total, pageSize, onPage }) {
  if (!total) return null;
  const from = (page - 1) * pageSize + 1;
  const to = Math.min(total, page * pageSize);
  return <div className="pagination" data-testid="pagination-bar">
    <span data-testid="pagination-summary">Showing <b>{from}</b>–<b>{to}</b> of <b>{total}</b></span>
    <div className="pagination-controls">
      <button data-testid="pagination-prev" className="button button-ghost" disabled={page <= 1} onClick={() => onPage(page - 1)}>Prev</button>
      <span data-testid="pagination-page">Page <b>{page}</b> / {pages}</span>
      <button data-testid="pagination-next" className="button button-ghost" disabled={page >= pages} onClick={() => onPage(page + 1)}>Next</button>
    </div>
  </div>;
}
function TemperatureBadge({ lead, onSaved, testIdPrefix = "temp", hideScore = false }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const locked = !!lead.temperature_locked;
  const setTemp = async (value) => {
    setBusy(true);
    try {
      const body = value === "AUTO" ? { mode: "auto" } : { temperature: value, mode: "manual" };
      await axios.post(`${API}/leads/${lead.lead_id}/temperature`, body);
      setOpen(false);
      await onSaved?.();
    } finally { setBusy(false); }
  };
  const badgeBtn = <button
    data-testid={`${testIdPrefix}-badge`}
    type="button"
    className={`badge badge-${scoreTone(lead.lead_score)} temp-badge-btn${locked ? " locked" : ""}`}
    onClick={(e) => { e.stopPropagation(); setOpen(v => !v); }}
    title={locked ? "Manually set — click to change or reset to auto" : "Auto-computed — click to override"}
  >
    {lead.lead_temperature}{locked ? " · manual" : ""}
  </button>;
  return <div className="temp-badge-wrap" data-testid={`${testIdPrefix}-wrap`}>
    {hideScore ? badgeBtn : <div className="score"><b>{lead.lead_score}</b>{badgeBtn}</div>}
    {open && <div className="temp-menu" data-testid={`${testIdPrefix}-menu`} onClick={e => e.stopPropagation()}>
      {["HOT", "WARM", "COLD"].map(t => (
        <button key={t} data-testid={`${testIdPrefix}-option-${t}`} className="temp-menu-item" disabled={busy} onClick={() => setTemp(t)}>
          <span className={`badge badge-${scoreTone(t === "HOT" ? 90 : t === "WARM" ? 60 : 20)}`}>{t}</span>
        </button>
      ))}
      <div className="temp-menu-divider" />
      <button data-testid={`${testIdPrefix}-option-AUTO`} className="temp-menu-item" disabled={busy} onClick={() => setTemp("AUTO")}>
        Reset to auto
      </button>
    </div>}
  </div>;
}


function LeadDetailModal({ lead, authUser, onClose, onRefresh }) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => { document.removeEventListener("keydown", onKey); document.body.style.overflow = ""; };
  }, [onClose]);
  return <div className="modal-shell lead-modal-shell" data-testid="lead-detail-modal" onClick={onClose}>
    <div className="modal-card lead-modal-card" onClick={e => e.stopPropagation()}>
      <button data-testid="lead-detail-modal-close" className="lead-modal-close" onClick={onClose} aria-label="Close"><X size={16} /></button>
      <LeadDetail lead={lead} authUser={authUser} onRefresh={onRefresh} onCloseModal={onClose} inModal={true} />
    </div>
  </div>;
}


function BulkConvertModal({ leads, agents, busy, onCancel, onSubmit }) {
  const [rows, setRows] = useState(() => leads.map(l => ({ lead_id: l.lead_id, customer_name: l.customer_name, phone_number: l.phone_number, deal_value: "", converted_by: "", notes: "" })));
  const [err, setErr] = useState("");
  const patch = (idx, key, val) => setRows(prev => prev.map((r, i) => i === idx ? { ...r, [key]: val } : r));
  const validRows = rows.filter(r => Number(r.deal_value) > 0 && r.converted_by);
  const canSubmit = rows.length > 0 && validRows.length === rows.length;
  const submit = async () => {
    setErr("");
    if (!canSubmit) { setErr("Fill Deal value (> 0) and Closed by for every customer before confirming."); return; }
    try {
      await onSubmit(rows.map(r => ({
        lead_id: r.lead_id,
        deal_value: Number(r.deal_value),
        converted_by: r.converted_by,
        notes: (r.notes || "").trim() || null,
      })));
    } catch (e) {
      setErr(e.response?.data?.detail?.message || e.response?.data?.detail || "Bulk conversion failed");
    }
  };
  return <div className="modal-shell bulk-convert-shell" data-testid="bulk-convert-modal" onClick={onCancel}>
    <div className="modal-card bulk-convert-card" onClick={e => e.stopPropagation()}>
      <div className="bulk-convert-head">
        <div>
          <h3>Convert {rows.length} lead{rows.length === 1 ? "" : "s"} to active</h3>
          <p className="muted">Fill in the closing details for each customer. Every field marked * is required before you can confirm.</p>
        </div>
        <div className="bulk-convert-progress" data-testid="bulk-convert-progress"><b>{validRows.length}</b> of <b>{rows.length}</b> filled</div>
      </div>
      <div className="bulk-convert-list">
        {rows.map((row, idx) => (
          <div key={row.lead_id} className={`bulk-convert-row${(Number(row.deal_value) > 0 && row.converted_by) ? " complete" : ""}`} data-testid={`bulk-convert-row-${row.lead_id}`}>
            <div className="bulk-convert-lead">
              <div className="lead-mini-avatar">{initials(row.customer_name)}</div>
              <div>
                <strong>{row.customer_name}</strong>
                <span>{row.phone_number}</span>
              </div>
            </div>
            <div className="bulk-convert-fields">
              <label>
                <span>Closed by *</span>
                <select data-testid={`bulk-convert-closedby-${row.lead_id}`} value={row.converted_by} onChange={e => patch(idx, "converted_by", e.target.value)}>
                  <option value="">— Select —</option>
                  <option value="AI">AI</option>
                  {agents.map(a => <option key={a} value={a}>{a}</option>)}
                </select>
              </label>
              <label>
                <span>Deal value (₹) *</span>
                <input data-testid={`bulk-convert-deal-${row.lead_id}`} type="number" min="1" step="1" value={row.deal_value} onChange={e => patch(idx, "deal_value", e.target.value)} placeholder="e.g. 4999" />
              </label>
              <label className="bulk-convert-notes-label">
                <span>Notes</span>
                <input data-testid={`bulk-convert-notes-${row.lead_id}`} type="text" value={row.notes} onChange={e => patch(idx, "notes", e.target.value)} placeholder="Optional context" />
              </label>
            </div>
          </div>
        ))}
      </div>
      {err && <div className="funnel-error" data-testid="bulk-convert-error">{err}</div>}
      <div className="modal-actions">
        <button className="button button-ghost" data-testid="bulk-convert-cancel" onClick={onCancel} disabled={busy}>Cancel</button>
        <button data-testid="bulk-convert-confirm" className="button button-success" disabled={busy || !canSubmit} onClick={submit}>{busy ? "Converting…" : `Confirm & convert ${rows.length}`}</button>
      </div>
    </div>
  </div>;
}


function LeadDetail({ lead, authUser, onRefresh, onCloseModal }) {
  const [tab, setTab] = useState("profile");
  const [editing, setEditing] = useState(false);
  const [showDelete, setShowDelete] = useState(false);
  const [showActive, setShowActive] = useState(false);
  const [activeBusy, setActiveBusy] = useState(false);
  const [activeMenuOpen, setActiveMenuOpen] = useState(false);
  const [showLost, setShowLost] = useState(false);
  const [lostBusy, setLostBusy] = useState(false);
  const [agents, setAgents] = useState([]);
  useEffect(() => {
    if (showActive && agents.length === 0) {
      axios.get(`${API}/team/agents`).then(r => setAgents(r.data || [])).catch(() => setAgents([]));
    }
  }, [showActive, agents.length]);
  const [notes, setNotes] = useState([]);
  const [reply, setReply] = useState("");
  const [replyBusy, setReplyBusy] = useState(false);
  const [replyNotice, setReplyNotice] = useState("");
  const [reminderBusy, setReminderBusy] = useState(false);
  const [reminderNotice, setReminderNotice] = useState("");
  const canManageActive = authUser?.role === "ADMIN" || authUser?.role === "SALES_MANAGER" || authUser?.role === "SALES_EXECUTIVE";
  const isAdmin = authUser?.role === "ADMIN";
  // PRD 2026-03-09: any state-change action (mark-active / mark-lost / mark-wrong-classification)
  // requires a non-empty lead_source on the lead. Backend enforces this too, but we short-
  // circuit on the client so the user sees the error without a wasted round trip.
  const [sourceGateError, setSourceGateError] = useState("");
  const _leadSourceOK = () => {
    const hasSource = !!(lead && lead.lead_source && String(lead.lead_source).trim());
    if (!hasSource) {
      setSourceGateError("Please select a Lead Source before continuing.");
      setEditing(true);           // pop the Edit modal so the user can set the source right away
      return false;
    }
    setSourceGateError("");
    return true;
  };
  useEffect(() => { setSourceGateError(""); }, [lead?.lead_source, lead?.lead_id]);
  useEffect(() => {
    if (!lead?.lead_id) return;
    axios.get(`${API}/leads/${lead.lead_id}/notes`).then(r => setNotes(r.data)).catch(console.error);
    setTab("profile"); setEditing(false); setShowDelete(false); setShowActive(false); setReply(""); setReplyNotice(""); setReminderNotice("");
  }, [lead?.lead_id]);
  const remove = async () => {
    await axios.delete(`${API}/leads/${lead.lead_id}`);
    setShowDelete(false);
    await onRefresh();
    if (onCloseModal) onCloseModal();
  };
  const markActive = async ({ deal_value, converted_by, notes }) => {
    setActiveBusy(true);
    try {
      await axios.post(`${API}/leads/${lead.lead_id}/mark-active`, { deal_value: Number(deal_value), converted_by, notes });
      setShowActive(false);
      await onRefresh();
      // Atomic-move: the lead has been deleted from the leads collection. Close the modal so the
      // user isn't left staring at a stale record.
      if (onCloseModal) onCloseModal();
    }
    finally { setActiveBusy(false); }
    // NOTE: no catch — MarkActiveForm's submit() wraps this call in try/catch to show axios errors.
  };
  const markWrongClassification = async () => {
    // PRD 2026-03-09 (revised): Wrong Classification is EXEMPT from the Lead Source gate.
    setActiveBusy(true); setActiveMenuOpen(false);
    try {
      await axios.post(`${API}/leads/${lead.lead_id}/mark-wrong-classification`);
      await onRefresh();
      if (onCloseModal) onCloseModal();
    }
    finally { setActiveBusy(false); }
  };
  const markLost = async ({ lost_reason, notes }) => {
    setLostBusy(true);
    try {
      await axios.post(`${API}/leads/${lead.lead_id}/mark-lost`, { lost_reason, notes });
      setShowLost(false);
      await onRefresh();
      if (onCloseModal) onCloseModal();
    }
    finally { setLostBusy(false); }
  };
  const sendReply = async () => {
    if (!reply.trim()) return;
    setReplyBusy(true); setReplyNotice("");
    try {
      const { data } = await axios.post(`${API}/leads/${lead.lead_id}/send-message`, { text: reply.trim() });
      setReplyNotice(data.delivered ? "Delivered via Interakt" : `${data.mode.replace("_", " ")} — see conversation log`);
      setReply(""); await onRefresh();
    } catch (err) {
      setReplyNotice(err.response?.data?.detail || "Send failed");
    } finally { setReplyBusy(false); }
  };
  const sendCustomerReminder = async () => {
    setReminderBusy(true); setReminderNotice("");
    try {
      const payload = reply.trim() ? { text: reply.trim() } : {};
      const { data } = await axios.post(`${API}/leads/${lead.lead_id}/send-followup-reminder`, payload);
      setReminderNotice(data.delivered ? "Follow-up reminder delivered to customer via Interakt" : `${data.mode.replace("_", " ")} — see conversation log`);
      setReply(""); await onRefresh();
    } catch (err) {
      setReminderNotice(err.response?.data?.detail || "Reminder failed");
    } finally { setReminderBusy(false); }
  };
  // PRD 2026-03-09: any contact that's currently closed (lost / converted / archived under
  // any reason) can be reopened as a fresh NEW LEAD. Preserves conversation history, notes,
  // follow_up_count, created_at etc. — see backend `POST /leads/{id}/reopen`.
  const isLostLead = (lead.sales_stage || "").toUpperCase() === "LOST";
  const isConvertedLead = !!lead.converted || (lead.sales_stage || "").toUpperCase() === "CONVERTED";
  const isArchivedContact = !!lead.archived_reason;
  const canReopen = canManageActive && (isLostLead || isConvertedLead || isArchivedContact);
  const [reopenBusy, setReopenBusy] = useState(false);
  const [reopenNotice, setReopenNotice] = useState("");
  const reopenAsLead = async () => {
    if (!window.confirm("Re-open this contact as a NEW LEAD in the active pipeline?\n\nAll existing history, notes, and follow-up data will be preserved.")) return;
    setReopenBusy(true); setReopenNotice("");
    try {
      const { data } = await axios.post(`${API}/leads/${lead.lead_id}/reopen`);
      setReopenNotice(data.path === "restored_from_archive" ? "Restored from archive to active pipeline" : "Reopened in place");
      await onRefresh();
      if (onCloseModal) onCloseModal();
    } catch (err) {
      setReopenNotice(err.response?.data?.detail || "Could not reopen this contact");
    } finally { setReopenBusy(false); }
  };
  return <aside className="panel detail-panel">
    <div className="detail-header">
      <div><div className="eyebrow">LEAD PROFILE</div><h2>{lead.customer_name}</h2><span>{lead.phone_number} · {lead.source}</span></div>
      <div className="detail-actions">
        {canManageActive && <div className="split-button" data-testid="mark-active-split">
          <button data-testid="mark-active-button" className="button button-success" disabled={activeBusy} onClick={() => { setActiveMenuOpen(v => !v); }} title="Move this lead to Active Customers"><ShieldCheck size={13} /> {activeBusy ? "Working…" : "Mark active"} <ChevronRight size={11} className={`split-caret${activeMenuOpen ? " open" : ""}`} /></button>
          {activeMenuOpen && <div className="split-menu" data-testid="mark-active-menu" onClick={e => e.stopPropagation()}>
            <button data-testid="mark-active-option-convert" className="split-menu-item" onClick={() => { if (!_leadSourceOK()) return; setActiveMenuOpen(false); setShowActive(true); }}>
              <strong>Mark as Active Customer</strong>
              <small>Intentional conversion — captures deal value, closed by, and notes</small>
            </button>
            <button data-testid="mark-active-option-wrong" className="split-menu-item danger" disabled={activeBusy} onClick={markWrongClassification}>
              <strong>Wrong Classification</strong>
              <small>Skip the form — lead was misclassified; add message as active_customer training data</small>
            </button>
          </div>}
        </div>}
        {canManageActive && <button data-testid="mark-lost-button" className="button button-danger" onClick={() => { if (_leadSourceOK()) setShowLost(true); }} title="Move this lead to Lost"><X size={13} /> Mark as lost</button>}
        {canReopen && <button data-testid="reopen-lead-button" className="button button-success" disabled={reopenBusy} onClick={reopenAsLead} title="Reactivate this contact as a NEW LEAD"><RefreshCcw size={13} /> {reopenBusy ? "Reopening…" : "Re-open as Lead"}</button>}
        {canManageActive && <button data-testid="edit-lead-button" className="button button-ghost" onClick={() => setEditing(true)}>Edit</button>}
        {isAdmin && <button data-testid="delete-lead-button" className="button button-danger" onClick={() => setShowDelete(true)}><Trash2 size={13} /> Delete</button>}
      </div>
    </div>
    {sourceGateError && <div className="lead-source-gate-error" data-testid="lead-source-gate-error" role="alert">{sourceGateError}</div>}
    {reopenNotice && <div className="reopen-notice" data-testid="reopen-notice" role="status">{reopenNotice}</div>}
    <div className="detail-tabs">
      <button data-testid="tab-profile" className={tab === "profile" ? "active" : ""} onClick={() => setTab("profile")}>Profile</button>
      <button data-testid="tab-reply" className={tab === "reply" ? "active" : ""} onClick={() => setTab("reply")}>Reply</button>
      <button data-testid="tab-notes" className={tab === "notes" ? "active" : ""} onClick={() => setTab("notes")}>Notes & Activity <em>{notes.length}</em></button>
    </div>
    {tab === "profile" && <><div className="score-hero"><div><span>LEAD SCORE</span><strong>{lead.lead_score}<small>/100</small></strong></div><TemperatureBadge lead={lead} onSaved={onRefresh} testIdPrefix="detail-temp" hideScore={true} /></div><div className="detail-section"><div className="section-title"><Bot size={15} /> AI qualification</div><div className="detail-fields"><div><small>Intent</small><b>{lead.intent}</b></div><div><small>Sales stage</small><b>{lead.sales_stage}</b></div><div><small>Plan interest</small><b>{lead.plan_interest}</b></div><div><small>Location</small><b>{lead.location}</b></div></div></div><div className="detail-section action-box"><small>NEXT BEST ACTION</small><strong><Zap size={16} />{lead.next_best_action}</strong></div><div className="detail-section"><div className="section-title"><Users size={15} /> Assigned salesperson</div><div className="assignee"><div className="profile-avatar">{initials(lead.assigned_salesperson)}</div><div><b>{lead.assigned_salesperson}</b><small>Sales operations{lead.assignment_reason ? ` · ${lead.assignment_reason}` : ""}</small></div>{lead.assignment_reason?.startsWith("auto") && <Badge tone="blue" data-testid="lead-auto-assigned-badge">AI PICK</Badge>}</div></div></>}
    {tab === "reply" && <div className="detail-section reply-box"><div className="section-title"><MessageCircle size={15} /> Send WhatsApp to customer</div><p className="muted">Send a message to <b>{lead.phone_number}</b> via Interakt. Response mode depends on backend SHADOW_MODE.</p><textarea data-testid="reply-textarea" value={reply} onChange={e => setReply(e.target.value)} placeholder="Type your reply here…" rows={4} /><div className="reply-actions"><button data-testid="reply-send" className="button button-primary" disabled={replyBusy || !reply.trim()} onClick={sendReply}><PhoneCall size={13} /> {replyBusy ? "Sending…" : "Send reply via Interakt"}</button><button data-testid="send-customer-reminder" className="button button-whatsapp" disabled={reminderBusy} onClick={sendCustomerReminder} title="Sends a plain-text follow-up reminder to the customer's WhatsApp via Interakt. Uses the text above or a friendly default nudge if left blank."><Bell size={13} /> {reminderBusy ? "Sending…" : "Send WhatsApp reminder"}</button></div>{replyNotice && <div data-testid="reply-notice" className="reply-notice">{replyNotice}</div>}{reminderNotice && <div data-testid="reminder-notice" className="reply-notice">{reminderNotice}</div>}<div className="section-title convo-title"><MessageSquareText size={15} /> Conversation history</div>{(lead.conversation || []).map(m => <div key={m.id} className={`message ${m.sender}`}><small>{(m.sender || "").toUpperCase()} · {relTime(m.at)}{m.mode ? ` · ${m.mode.replace("_", " ")}` : ""}{m.kind === "customer_followup_reminder" ? " · REMINDER" : ""}</small><p>{m.text}</p></div>)}</div>}
    {tab === "notes" && <NotesTab lead={lead} notes={notes} onSaved={async () => { const r = await axios.get(`${API}/leads/${lead.lead_id}/notes`); setNotes(r.data); }} />}
    {editing && <EditLeadModal lead={lead} onClose={() => setEditing(false)} onSaved={async () => { setEditing(false); await onRefresh(); }} />}
    {showDelete && <div className="modal-shell" data-testid="delete-confirm-modal"><div className="modal-card"><h3>Delete this lead?</h3><p>This removes <b>{lead.customer_name}</b> and all their notes. This cannot be undone.</p><div className="modal-actions"><button className="button" onClick={() => setShowDelete(false)}>Cancel</button><button data-testid="delete-confirm-yes" className="button button-danger" onClick={remove}>Delete lead</button></div></div></div>}
    {showActive && <MarkActiveForm lead={lead} agents={agents} busy={activeBusy} onCancel={() => setShowActive(false)} onSubmit={markActive} />}
    {showLost && <MarkLostForm lead={lead} busy={lostBusy} onCancel={() => setShowLost(false)} onSubmit={markLost} />}
  </aside>;
}

function MarkActiveForm({ lead, agents, busy, onCancel, onSubmit }) {
  const [dealValue, setDealValue] = useState("");
  const [convertedBy, setConvertedBy] = useState("");
  const [notes, setNotes] = useState("");
  const [err, setErr] = useState("");
  const trimmedConvertedBy = (convertedBy || "").trim();
  const canSubmit = Number(dealValue) > 0 && !!trimmedConvertedBy && !busy;
  const submit = async () => {
    setErr("");
    if (!canSubmit) { setErr("Deal value and Converted by are required."); return; }
    try {
      await onSubmit({ deal_value: Number(dealValue), converted_by: trimmedConvertedBy, notes: (notes || "").trim() });
    }
    catch (e) { setErr(e?.response?.data?.detail || "Could not mark as active"); }
  };
  return <div className="modal-shell" data-testid="mark-active-modal" onClick={e => e.stopPropagation()}>
    <div className="modal-card" onClick={e => e.stopPropagation()}>
      <h3>Mark {lead.customer_name} as active</h3>
      <p className="muted">This archives the lead to <b>Active customers</b>. Fill in the conversion details below.</p>
      <label className="modal-label">Deal value (₹) *
        <input data-testid="deal-value-input" type="number" min="1" step="1" value={dealValue} onChange={e => setDealValue(e.target.value)} placeholder="e.g. 4999" />
      </label>
      <label className="modal-label">Converted by *
        <select data-testid="converted-by-select" value={convertedBy} onChange={e => setConvertedBy(e.target.value)}>
          <option value="">— Select —</option>
          <option value="AI">AI</option>
          {agents.map(a => <option key={a} value={a}>{a}</option>)}
        </select>
      </label>
      <label className="modal-label">Notes (optional)
        <textarea data-testid="mark-active-notes" value={notes} onChange={e => setNotes(e.target.value)} rows="3" placeholder="Any context on this conversion" />
      </label>
      {err && <div className="funnel-error" data-testid="mark-active-error">{err}</div>}
      <div className="modal-actions">
        <button className="button button-ghost" onClick={onCancel} disabled={busy}>Cancel</button>
        <button type="button" data-testid="mark-active-confirm" className="button button-success" disabled={!canSubmit} onClick={submit}>{busy ? "Archiving…" : "Confirm & mark active"}</button>
      </div>
    </div>
  </div>;
}

// Canonical Lost-reason master list (shared by Mark-Lost popup + Edit Lead form + Lead Database
// filter chip). Keep in sync with backend `LOST_REASON_KEYS`. Per PRD 2026-03-05.
const LOST_REASONS = [
  { value: "budget", label: "Budget" },
  { value: "food_taste", label: "Food Taste" },
  { value: "ghost_cnr", label: "Ghost - call disconnected/CNR" },
  { value: "non_serviceable_area", label: "Non Serviceable Area" },
  { value: "non_serviceable_requirement", label: "Non Serviceable Requirement" },
  { value: "competitor", label: "Competitor" },
  { value: "a_la_carte", label: "A la carte" },
  { value: "maid", label: "Maid" },
  { value: "break_no_requirement", label: "Break - no requirement" },
  { value: "just_enquiry", label: "Just Enquiry" },
  { value: "other", label: "Other" },
];
const LOST_REASON_LABEL = Object.fromEntries(LOST_REASONS.map(r => [r.value, r.label]));

function MarkLostForm({ lead, busy, onCancel, onSubmit }) {
  const [reason, setReason] = useState("");
  const [notes, setNotes] = useState("");
  const [err, setErr] = useState("");
  const submit = async () => {
    setErr("");
    if (!reason) { setErr("A lost reason is required."); return; }
    if (reason === "other" && !notes.trim()) { setErr("Please describe the reason in the notes below when selecting Other."); return; }
    try { await onSubmit({ lost_reason: reason, notes: notes.trim() }); }
    catch (e) { setErr(e.response?.data?.detail || "Could not mark as lost"); }
  };
  return <div className="modal-shell" data-testid="mark-lost-modal" onClick={e => e.stopPropagation()}>
    <div className="modal-card wide" onClick={e => e.stopPropagation()}>
      <h3>Mark {lead.customer_name} as lost</h3>
      <p className="muted">This flags the lead as <b>LOST</b> in the Lead Database (filter by Status → Lost to find it later).</p>
      <label className="modal-label">Lost reason *
        <select data-testid="lost-reason-select" size="6" className="lost-reason-select" value={reason} onChange={e => setReason(e.target.value)}>
          <option value="">— Select a reason —</option>
          {LOST_REASONS.map(r => <option key={r.value} value={r.value} data-testid={`lost-reason-option-${r.value}`}>{r.label}</option>)}
        </select>
      </label>
      <label className="modal-label">{reason === "other" ? "Reason details *" : "Additional notes (optional)"}
        <textarea data-testid="mark-lost-notes" value={notes} onChange={e => setNotes(e.target.value)} rows="3" placeholder={reason === "other" ? "Describe the specific reason this lead was lost" : "Add any context that will help future outreach"} />
      </label>
      {err && <div className="funnel-error" data-testid="mark-lost-error">{err}</div>}
      <div className="modal-actions">
        <button type="button" className="button button-ghost" onClick={onCancel} disabled={busy}>Cancel</button>
        <button type="button" data-testid="mark-lost-confirm" className="button button-danger" disabled={busy || !reason} onClick={submit}>{busy ? "Saving…" : "Confirm & mark lost"}</button>
      </div>
    </div>
  </div>;
}

function NotesTab({ lead, notes, onSaved }) {
  const [text, setText] = useState("");
  const [kind, setKind] = useState("note");
  const [outcome, setOutcome] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); if (!text.trim()) return;
    setBusy(true);
    try { await axios.post(`${API}/leads/${lead.lead_id}/notes`, { text: text.trim(), kind, call_outcome: kind === "call" ? outcome : null }); setText(""); setOutcome(""); await onSaved(); }
    finally { setBusy(false); }
  };
  return <div className="notes-tab">
    <form className="notes-form" onSubmit={submit}>
      <div className="notes-kind-row"><label><input type="radio" data-testid="notes-kind-note" name="kind" value="note" checked={kind === "note"} onChange={() => setKind("note")} /> Note</label><label><input type="radio" data-testid="notes-kind-call" name="kind" value="call" checked={kind === "call"} onChange={() => setKind("call")} /> Call log</label></div>
      <textarea data-testid="notes-text" value={text} onChange={e => setText(e.target.value)} placeholder={kind === "call" ? "What was discussed on the call?" : "Add a note about this lead…"} rows={3} required />
      {kind === "call" && <input data-testid="notes-outcome" placeholder="Call outcome (e.g. connected, no answer)" value={outcome} onChange={e => setOutcome(e.target.value)} />}
      <button data-testid="notes-submit" className="button button-primary" type="submit" disabled={busy || !text.trim()}>{busy ? "Saving…" : "Add to activity log"}</button>
    </form>
    {notes.length === 0 ? <div className="empty-state"><MessageSquareText size={26} /><strong>No activity yet</strong><span>Log the first call or note above.</span></div> :
      <ul className="notes-list">{notes.map(n => <li key={n.note_id} className={`notes-item ${n.kind}`} data-testid={`notes-item-${n.note_id}`}>
        <div className="notes-item-top"><Badge tone={n.kind === "call" ? "green" : "blue"}>{n.kind.toUpperCase()}</Badge><span>{n.author_name} · {relTime(n.created_at)}</span>{n.call_outcome && <span className="notes-outcome">Outcome: {n.call_outcome}</span>}</div>
        <p>{n.text}</p>
      </li>)}</ul>
    }
  </div>;
}

function EditLeadModal({ lead, onClose, onSaved }) {
  const isoToDate = (iso) => (iso ? new Date(iso).toISOString().slice(0, 10) : "");
  const isoToTime = (iso) => {
    if (!iso) return "";
    const d = new Date(iso);
    return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
  };
  const [form, setForm] = useState({
    customer_name: lead.customer_name || "",
    phone_number: lead.phone_number || "",
    assigned_salesperson: lead.assigned_salesperson || "",
    lead_source: lead.lead_source || "",
    next_follow_up_at: isoToDate(lead.next_follow_up_at),
    follow_up_reminder_time: isoToTime(lead.follow_up_reminder_at),
    deal_value: lead.deal_value ?? "",
    lost_reason: lead.lost_reason || "",
    payment_date: isoToDate(lead.payment_date),
    notes: "",
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [agents, setAgents] = useState([]);
  const [options, setOptions] = useState({ lead_sources: [] });
  useEffect(() => {
    axios.get(`${API}/team/agents`).then(r => setAgents([...new Set(Array.isArray(r.data) ? r.data : (r.data?.agents || []))])).catch(console.error);
    axios.get(`${API}/dashboard/lead-source-options`).then(r => setOptions(r.data)).catch(console.error);
  }, []);
  const submit = async (e) => {
    e.preventDefault(); setBusy(true); setErr("");
    try {
      const body = {
        customer_name: form.customer_name,
        phone_number: form.phone_number,
        assigned_salesperson: form.assigned_salesperson || "Unassigned",
        lead_source: form.lead_source || null,
        next_follow_up_at: form.next_follow_up_at ? `${form.next_follow_up_at}T10:00:00+00:00` : null,
        follow_up_reminder_at: (form.next_follow_up_at && form.follow_up_reminder_time)
          ? new Date(`${form.next_follow_up_at}T${form.follow_up_reminder_time}:00`).toISOString()
          : null,
        deal_value: form.deal_value === "" ? null : Number(form.deal_value),
        lost_reason: form.lost_reason || null,
        payment_date: form.payment_date ? `${form.payment_date}T10:00:00+00:00` : null,
      };
      await axios.patch(`${API}/leads/${lead.lead_id}`, body);
      // Optional note captured alongside the edit — same endpoint used elsewhere.
      const noteText = (form.notes || "").trim();
      if (noteText) {
        await axios.post(`${API}/leads/${lead.lead_id}/notes`, { text: noteText, kind: "note" });
      }
      await onSaved();
    }
    catch (er) { setErr(er.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };
  return <div className="modal-shell" data-testid="edit-lead-modal" onClick={onClose}>
    <form className="modal-card wide" onSubmit={submit} onClick={e => e.stopPropagation()}>
      <h3>Edit lead — {lead.customer_name}</h3>
      <div className="modal-grid">
        <label>Name<input data-testid="edit-name" value={form.customer_name} onChange={e => setForm({ ...form, customer_name: e.target.value })} required /></label>
        <label>Phone<input data-testid="edit-phone" value={form.phone_number} onChange={e => setForm({ ...form, phone_number: e.target.value })} required /></label>
        <label>Assigned to
          <select data-testid="edit-owner" value={form.assigned_salesperson} onChange={e => setForm({ ...form, assigned_salesperson: e.target.value })}>
            <option value="">— Select agent —</option>
            <option value="Unassigned">Unassigned</option>
            {agents.map(a => <option key={a} value={a}>{a}</option>)}
          </select>
        </label>
        <label>Lead source
          <select data-testid="edit-lead-source" value={form.lead_source} onChange={e => setForm({ ...form, lead_source: e.target.value })}>
            <option value="">— Select source —</option>
            {(options.lead_sources || []).map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        </label>
        <label>Follow-up date<input data-testid="edit-follow-up-date" type="date" value={form.next_follow_up_at} onChange={e => setForm({ ...form, next_follow_up_at: e.target.value })} /></label>
        <label title="When set, the assigned salesperson gets an in-app notification 15 minutes before this time on the follow-up date.">Reminder time <small style={{opacity: 0.6}}>(alerts 15 min prior)</small><input data-testid="edit-reminder-time" type="time" value={form.follow_up_reminder_time} disabled={!form.next_follow_up_at} onChange={e => setForm({ ...form, follow_up_reminder_time: e.target.value })} /></label>
        <label>Deal value (₹)<input data-testid="edit-deal-value" type="number" min="0" step="1" value={form.deal_value} onChange={e => setForm({ ...form, deal_value: e.target.value })} placeholder="e.g. 12000" /></label>
        <label>Lead lost reason
          <select data-testid="edit-lost-reason" value={form.lost_reason} onChange={e => setForm({ ...form, lost_reason: e.target.value })}>
            <option value="">— Not set —</option>
            {LOST_REASONS.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
          </select>
        </label>
        <label>Payment date<input data-testid="edit-payment-date" type="date" value={form.payment_date} onChange={e => setForm({ ...form, payment_date: e.target.value })} /></label>
        <label className="edit-notes-label">Notes<textarea data-testid="edit-notes" rows="3" value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} placeholder="Add a fresh note (appears in the lead's activity log)" /></label>
      </div>
      {err && <div className="login-error">{err}</div>}
      <div className="modal-actions"><button type="button" className="button" onClick={onClose}>Cancel</button><button data-testid="edit-save" className="button button-primary" type="submit" disabled={busy}>{busy ? "Saving…" : "Save changes"}</button></div>
    </form>
  </div>;
}

function AddLeadManuallyModal({ onClose, onSaved }) {
  const [form, setForm] = useState({
    customer_name: "",
    phone_number: "",
    assigned_salesperson: "",
    lead_source: "",
    next_follow_up_at: "",
    notes: "",
  });
  const [agents, setAgents] = useState([]);
  const [options, setOptions] = useState({ lead_sources: [] });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  // PRD 2026-03-09: hold the existing contact after a 409 so the modal can render a
  // clickable link straight to that contact's detail view.
  const [dupContact, setDupContact] = useState(null);
  useEffect(() => {
    axios.get(`${API}/team/agents`).then(r => setAgents([...new Set(Array.isArray(r.data) ? r.data : (r.data?.agents || []))])).catch(console.error);
    axios.get(`${API}/dashboard/lead-source-options`).then(r => setOptions(r.data)).catch(console.error);
  }, []);
  const submit = async (e) => {
    e.preventDefault(); setBusy(true); setErr(""); setDupContact(null);
    try {
      const body = {
        customer_name: form.customer_name.trim(),
        phone_number: form.phone_number.trim(),
        assigned_salesperson: form.assigned_salesperson || null,
        lead_source: form.lead_source || null,
        next_follow_up_at: form.next_follow_up_at ? `${form.next_follow_up_at}T10:00:00+00:00` : null,
        notes: form.notes.trim() || null,
      };
      await axios.post(`${API}/leads/manual`, body);
      await onSaved();
      onClose();
    }
    catch (er) {
      const detail = er.response?.data?.detail;
      if (detail && typeof detail === "object" && detail.existing_lead_id) {
        setDupContact(detail);
        setErr(detail.message || "A contact with this phone number already exists");
      } else {
        setErr(typeof detail === "string" ? detail : "Could not add lead");
      }
    }
    finally { setBusy(false); }
  };
  const openExistingContact = async () => {
    try {
      const r = await axios.get(`${API}/leads/${dupContact.existing_lead_id}`);
      if (window.__openLeadDetail) window.__openLeadDetail(r.data);
      onClose();
    } catch { setErr("Could not load that contact. It may have been deleted."); }
  };
  return <div className="modal-shell" data-testid="add-lead-modal" onClick={onClose}>
    <form className="modal-card wide" onSubmit={submit} onClick={e => e.stopPropagation()}>
      <h3>Add lead manually</h3>
      <p className="muted">For customers who reached out via call, referral, walk-in, or any channel outside Interakt.</p>
      <div className="modal-grid">
        <label>Name *<input data-testid="add-lead-name" value={form.customer_name} onChange={e => setForm({ ...form, customer_name: e.target.value })} required autoFocus /></label>
        <label>Phone *<input data-testid="add-lead-phone" value={form.phone_number} onChange={e => setForm({ ...form, phone_number: e.target.value })} required placeholder="+91 …" /></label>
        <label>Assigned to
          <select data-testid="add-lead-owner" value={form.assigned_salesperson} onChange={e => setForm({ ...form, assigned_salesperson: e.target.value })}>
            <option value="">Unassigned</option>
            {agents.map(a => <option key={a} value={a}>{a}</option>)}
          </select>
        </label>
        <label>Lead source *
          <select data-testid="add-lead-source" value={form.lead_source} onChange={e => setForm({ ...form, lead_source: e.target.value })} required>
            <option value="">— Select source —</option>
            {(options.lead_sources || []).map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        </label>
        <label>Follow-up date<input data-testid="add-lead-follow-up" type="date" value={form.next_follow_up_at} onChange={e => setForm({ ...form, next_follow_up_at: e.target.value })} /></label>
        <label className="edit-notes-label">Notes<textarea data-testid="add-lead-notes" rows="3" value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} placeholder="What did they ask about? What did we promise?" /></label>
      </div>
      {err && !dupContact && <div className="login-error" data-testid="add-lead-error">{err}</div>}
      {dupContact && <div className="add-lead-dup" data-testid="add-lead-dup-error" role="alert">
        <div><strong>{err}</strong></div>
        <div className="add-lead-dup-detail">
          <button type="button" data-testid="add-lead-dup-open" className="add-lead-dup-link" onClick={openExistingContact}>
            Open {dupContact.existing_name || "existing contact"} ({dupContact.existing_lead_id})
          </button>
          <span className="add-lead-dup-status">status: {(dupContact.existing_status || "existing").replaceAll("_", " ")}</span>
        </div>
      </div>}
      <div className="modal-actions">
        <button type="button" className="button" onClick={onClose}>Cancel</button>
        <button type="submit" data-testid="add-lead-submit" className="button button-primary" disabled={busy || !form.customer_name.trim() || !form.phone_number.trim() || !form.lead_source}>{busy ? "Adding…" : "Add lead"}</button>
      </div>
    </form>
  </div>;
}


function Team({ currentUser }) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "SALES_EXECUTIVE", phone_number: "" });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [busyId, setBusyId] = useState(null);
  const [editingPhoneId, setEditingPhoneId] = useState(null);
  const [phoneDraft, setPhoneDraft] = useState("");
  const roles = ["ADMIN", "SALES_MANAGER", "SALES_EXECUTIVE", "SUPPORT", "VIEW_ONLY"];
  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await axios.get(`${API}/users`); setUsers(r.data); } finally { setLoading(false); }
  }, []);
  useEffect(() => { load().catch(err => setError(err.response?.data?.detail || "Failed to load team")); }, [load]);
  const submit = async (e) => {
    e.preventDefault(); setSaving(true); setError(""); setNotice("");
    try {
      await axios.post(`${API}/auth/register`, form);
      setNotice(`${form.email} created`);
      setForm({ name: "", email: "", password: "", role: "SALES_EXECUTIVE", phone_number: "" });
      await load();
    } catch (err) {
      const d = err.response?.data?.detail;
      setError(typeof d === "string" ? d : "Unable to create account");
    } finally { setSaving(false); }
  };
  const savePhone = async (u) => {
    setBusyId(u.user_id); setError(""); setNotice("");
    try {
      await axios.patch(`${API}/users/${u.user_id}`, { phone_number: phoneDraft });
      setNotice(`${u.name || u.email} phone updated`);
      setEditingPhoneId(null); setPhoneDraft("");
      await load();
    } catch (err) {
      setError(err.response?.data?.detail || "Unable to save phone");
    } finally { setBusyId(null); }
  };
  const remove = async (u) => {
    if (u.user_id === currentUser.user_id) return;
    setBusyId(u.user_id); setError(""); setNotice("");
    try { await axios.delete(`${API}/users/${u.user_id}`); setNotice(`${u.email} removed`); await load(); }
    catch (err) { setError(err.response?.data?.detail || "Unable to remove account"); }
    finally { setBusyId(null); }
  };
  return <><PageHeader eyebrow="ADMIN / TEAM MANAGEMENT" title="Team" subtitle="Invite salespeople, managers and support agents. Only admins can view or change this list." action={<Badge tone="purple">{users.length} member{users.length === 1 ? "" : "s"}</Badge>} />
    <div className="team-grid">
      <section className="panel team-form-panel">
        <div className="section-title"><UserPlus size={16} /> Invite a team member</div>
        <form className="team-form" onSubmit={submit}>
          <label>Full name</label>
          <input data-testid="team-form-name" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="e.g. Priya Nair" required />
          <label>Email</label>
          <input data-testid="team-form-email" type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} placeholder="priya@paushtikk.com" required />
          <label>Temporary password</label>
          <input data-testid="team-form-password" type="password" value={form.password} onChange={e => setForm({ ...form, password: e.target.value })} placeholder="At least 8 characters" minLength={8} required />
          <label>Role</label>
          <select data-testid="team-form-role" value={form.role} onChange={e => setForm({ ...form, role: e.target.value })}>{roles.map(r => <option key={r} value={r}>{r.replaceAll("_", " ")}</option>)}</select>
          <label>WhatsApp number (for follow-up reminders)</label>
          <input data-testid="team-form-phone" value={form.phone_number} onChange={e => setForm({ ...form, phone_number: e.target.value })} placeholder="+91 98xxx xxxxx" />
          {error && <div data-testid="team-form-error" className="login-error">{error}</div>}
          {notice && <div data-testid="team-form-success" className="team-form-notice">{notice}</div>}
          <button data-testid="team-form-submit" className="button button-primary full" type="submit" disabled={saving}>{saving ? "Creating…" : "Create account"}</button>
        </form>
      </section>
      <section className="panel team-list-panel">
        <div className="section-title"><Users size={16} /> Current team</div>
        {loading ? <div className="empty-state"><Users size={26} /><strong>Loading team…</strong></div> :
          users.length === 0 ? <div className="empty-state"><Users size={26} /><strong>No team members yet</strong><span>Create the first salesperson account on the left.</span></div> :
          <ul className="team-list">
            {users.map(u => <li key={u.user_id} className="team-row" data-testid={`team-row-${u.user_id}`}>
              <div className="team-avatar">{initials(u.name || u.email)}</div>
              <div className="team-body">
                <div className="team-top"><strong data-testid={`team-name-${u.user_id}`}>{u.name}</strong><Badge tone={roleTone(u.role)} data-testid={`team-role-${u.user_id}`}>{u.role.replaceAll("_", " ")}</Badge>{u.user_id === currentUser.user_id && <Badge tone="amber">YOU</Badge>}</div>
                <span className="team-email" data-testid={`team-email-${u.user_id}`}>{u.email}</span>
                {editingPhoneId === u.user_id ? (
                  <span className="team-phone-edit">
                    <input data-testid={`team-phone-input-${u.user_id}`} value={phoneDraft} onChange={e => setPhoneDraft(e.target.value)} placeholder="+91 98xxx xxxxx" />
                    <button data-testid={`team-phone-save-${u.user_id}`} className="button button-primary" disabled={busyId === u.user_id} onClick={() => savePhone(u)}>{busyId === u.user_id ? "Saving…" : "Save"}</button>
                    <button className="button" onClick={() => { setEditingPhoneId(null); setPhoneDraft(""); }}>Cancel</button>
                  </span>
                ) : (
                  <span className="team-phone" data-testid={`team-phone-${u.user_id}`}>
                    <Phone size={11} /> {u.phone_number || <em>no WhatsApp number</em>}
                    <button data-testid={`team-phone-edit-${u.user_id}`} className="text-button" onClick={() => { setEditingPhoneId(u.user_id); setPhoneDraft(u.phone_number || ""); }}>{u.phone_number ? "Edit" : "Add"}</button>
                  </span>
                )}
                <span className="team-created">Joined {u.created_at ? new Date(u.created_at).toLocaleDateString() : "—"}</span>
              </div>
              <button data-testid={`team-delete-${u.user_id}`} className="icon-button team-delete" disabled={u.user_id === currentUser.user_id || busyId === u.user_id} onClick={() => remove(u)} title={u.user_id === currentUser.user_id ? "You cannot delete your own account" : "Remove team member"}><Trash2 size={14} /></button>
            </li>)}
          </ul>
        }
      </section>
    </div>
  </>;
}

function Webhooks() { const [items, setItems] = useState([]); useEffect(() => { axios.get(`${API}/webhooks`).then(r => setItems(r.data)).catch(console.error); }, []); return <><PageHeader eyebrow="INTEGRATIONS / INTERAKT" title="Webhook events" subtitle="Inbound events are captured, deduplicated, and kept in shadow mode." action={<div className="health"><i /> Endpoint ready</div>} /><section className="panel table-panel"><div className="toolbar"><div><h2>Event log</h2><p>Waiting for production Interakt credentials and documented endpoints.</p></div><Badge tone="amber">SHADOW MODE</Badge></div>{items.length ? <div className="table-wrap"><table><thead><tr><th>Event key</th><th>Type</th><th>Status</th><th>Received</th></tr></thead><tbody>{items.map(x => <tr data-testid={`webhook-event-row-${x.event_key}`} key={x.event_key}><td data-testid={`webhook-event-key-${x.event_key}`}>{x.event_key}</td><td data-testid={`webhook-event-type-${x.event_key}`}>{x.event_type}</td><td><Badge tone="green">RECEIVED</Badge></td><td data-testid={`webhook-event-time-${x.event_key}`}>{new Date(x.timestamp).toLocaleString()}</td></tr>)}</tbody></table></div> : <div className="empty-state"><Webhook size={32} /><strong>No webhook events yet</strong><span>Send a signed Interakt event to /api/webhooks/interakt.</span></div>}</section></>; }
function Settings({ authUser }) {
  const [integration, setIntegration] = useState({ webhook_secret_configured: false, base_url_configured: false, api_key_configured: false, shadow_mode: true, auto_reply_confidence: 0.85 });
  const [purgeText, setPurgeText] = useState("");
  const [purging, setPurging] = useState(false);
  const [purgeResult, setPurgeResult] = useState(null);
  useEffect(() => { axios.get(`${API}/integration-status`).then(r => setIntegration(r.data)).catch(() => {}); }, []);
  const webhookLive = integration.webhook_secret_configured;
  const shadow = integration.shadow_mode;
  const isAdmin = authUser?.role === "ADMIN";

  const handlePurge = async () => {
    if (purgeText !== "DELETE") return;
    if (!window.confirm("This will permanently delete ALL leads, webhook events, lead notes, and archived leads. This action CANNOT be undone. Continue?")) return;
    setPurging(true);
    setPurgeResult(null);
    try {
      const r = await axios.post(`${API}/admin/purge-leads-data`, { confirmation: "DELETE" });
      setPurgeResult({ ok: true, ...r.data });
      setPurgeText("");
    } catch (e) {
      setPurgeResult({ ok: false, error: e?.response?.data?.detail || e.message });
    } finally {
      setPurging(false);
    }
  };

  return <><PageHeader eyebrow="WORKSPACE / CONTROL PLANE" title="Settings" subtitle="Control how the intelligence layer works for your sales team." /><div className="settings-grid"><section className="panel settings-card"><div className="section-title"><Settings2 size={16} /> Operating mode</div><div className="setting-row"><div><strong>AI response mode</strong><small data-testid="operating-mode-detail">{shadow ? "SHADOW MODE — analysis runs but no messages are sent." : "LIVE MODE — approved replies POST to Interakt. Set SHADOW_MODE=true in backend .env to disable."}</small></div><Badge tone={shadow ? "amber" : "green"} data-testid="operating-mode-badge">{shadow ? "SHADOW MODE" : "LIVE MODE"}</Badge></div><div className="setting-row"><div><strong>AI confidence threshold</strong><small>Automatic replies fire only when AI confidence is at or above this level (set via AUTO_REPLY_CONFIDENCE).</small></div><strong data-testid="confidence-threshold">{Math.round((integration.auto_reply_confidence || 0.85) * 100)}%</strong></div><div className="setting-row"><div><strong>Interakt webhook signature</strong><small data-testid="webhook-status-detail">{webhookLive ? "Verified with INTERAKT_WEBHOOK_SECRET. Unsigned requests are rejected with 401." : "Set INTERAKT_WEBHOOK_SECRET in backend .env to enforce signed webhooks."}</small></div><Badge tone={webhookLive ? "green" : "amber"} data-testid="webhook-status-badge">{webhookLive ? "LIVE" : "PENDING"}</Badge></div><div className="setting-row"><div><strong>Interakt API base URL</strong><small>{integration.base_url_configured ? "Configured — outbound requests target this base URL." : "Add INTERAKT_BASE_URL to prepare outbound WhatsApp calls."}</small></div><Badge tone={integration.base_url_configured ? "green" : "amber"}>{integration.base_url_configured ? "SET" : "PENDING"}</Badge></div><div className="setting-row"><div><strong>Interakt API key (outbound)</strong><small data-testid="api-key-status-detail">{integration.api_key_configured ? (shadow ? "Configured — Shadow Mode currently blocks automatic delivery." : "Configured — approved replies are sent through Interakt automatically.") : "Add INTERAKT_API_KEY in backend .env to enable outbound WhatsApp replies via Interakt."}</small></div><Badge tone={integration.api_key_configured ? "green" : "amber"} data-testid="api-key-status-badge">{integration.api_key_configured ? "LIVE" : "PENDING"}</Badge></div></section><section className="panel settings-card"><div className="section-title"><Users size={16} /> Signed-in account</div><div className="setting-row"><div><strong data-testid="settings-user-name">{authUser.name}</strong><small>{authUser.email}</small></div><Badge tone="blue" data-testid="settings-user-role">{authUser.role.replaceAll("_", " ")}</Badge></div><div className="notice"><CircleHelp size={16} /><span>Sign in as a specific salesperson from the sidebar to see only their follow-up queue on the dashboard.</span></div><div className="setting-row"><div><strong>First-time walkthrough</strong><small>Restart the guided tour of Leads, Active customers, filters, and follow-ups.</small></div><button data-testid="replay-tutorial-button" className="button button-ghost" onClick={async () => { try { await axios.post(`${API}/users/me/onboarding`, { completed: false }); window.dispatchEvent(new Event("onboarding:replay")); } catch (_) { /* replay handled optimistically */ } }}>Replay tutorial</button></div></section>
    {isAdmin && <section className="panel settings-card danger-card" data-testid="danger-zone-panel">
      <div className="section-title danger-title"><Trash2 size={16} /> Danger zone</div>
      <div className="setting-row">
        <div>
          <strong>Clear all leads &amp; webhook events</strong>
          <small>Permanently deletes every lead, webhook event, lead note, and archived lead from the database. Users, knowledge base, and integration settings stay intact. This cannot be undone.</small>
        </div>
        <Badge tone="red">ADMIN ONLY</Badge>
      </div>
      <div className="danger-actions">
        <input
          data-testid="purge-confirm-input"
          type="text"
          value={purgeText}
          onChange={e => setPurgeText(e.target.value)}
          placeholder='Type DELETE to enable'
          disabled={purging}
          className="danger-input"
        />
        <button
          data-testid="purge-data-button"
          className="button button-danger"
          onClick={handlePurge}
          disabled={purgeText !== "DELETE" || purging}
        >
          {purging ? "Purging…" : "Clear all data"}
        </button>
      </div>
      {purgeResult?.ok && <div className="notice notice-success" data-testid="purge-success">
        <ShieldCheck size={16} />
        <span>Purge complete — leads: {purgeResult.purged?.leads ?? 0}, webhook events: {purgeResult.purged?.webhook_events ?? 0}, notes: {purgeResult.purged?.lead_notes ?? 0}, archived: {purgeResult.purged?.archived_leads ?? 0}.</span>
      </div>}
      {purgeResult && purgeResult.ok === false && <div className="notice notice-error" data-testid="purge-error">
        <X size={16} />
        <span>{purgeResult.error}</span>
      </div>}
    </section>}
  </div></>;
}

// -------- Shared list filters used by Leads DB and Active Customers --------

const DATE_RANGE_PRESETS = [
  { id: "all", label: "All" },
  { id: "today", label: "Today" },
  { id: "this_week", label: "This week" },
  { id: "this_month", label: "This month" },
  { id: "custom", label: "Custom" },
];
const CATEGORY_OPTIONS = ["all", "new_lead", "marketing_lead", "active_customer"];
const SOURCE_OPTIONS = ["all", "keyword", "llm", "interakt_tag"];
const STAGE_OPTIONS = ["all", "NEW LEAD", "AI QUALIFYING", "QUALIFIED", "PAYMENT PENDING", "CONVERTED", "HUMAN HANDOVER", "LOST"];
const FOLLOW_UP_OPTIONS = [
  { id: "all", label: "All follow-ups" },
  { id: "overdue", label: "Overdue" },
  { id: "today", label: "Today" },
  { id: "upcoming", label: "Upcoming" },
];

const emptyFilters = () => ({
  search: "", date_range: "all", date_from: "", date_to: "",
  category: "all", classification_source: "all", agent: "all",
  sales_stage: "all", follow_up: "all",
});

function useSessionFilters(key) {
  const [filters, setFilters] = useState(() => {
    try {
      const raw = sessionStorage.getItem(key);
      return raw ? { ...emptyFilters(), ...JSON.parse(raw) } : emptyFilters();
    } catch { return emptyFilters(); }
  });
  useEffect(() => {
    try { sessionStorage.setItem(key, JSON.stringify(filters)); } catch (_) { /* session storage full */ }
  }, [key, filters]);
  return [filters, setFilters];
}

function ListFilters({ filters, setFilters, agents = [], testIdPrefix = "list-filter" }) {
  const set = (patch) => setFilters(f => ({ ...f, ...patch }));
  return <div className="list-filters" data-testid={`${testIdPrefix}-panel`}>
    <div className="search-box" data-testid={`${testIdPrefix}-search-box`}>
      <Search size={14} />
      <input data-testid={`${testIdPrefix}-search`} value={filters.search} onChange={e => set({ search: e.target.value })} placeholder="Search name or phone…" />
    </div>
    <div className="filter-chips" data-testid={`${testIdPrefix}-date-chips`}>
      {DATE_RANGE_PRESETS.map(p => (
        <button key={p.id} data-testid={`${testIdPrefix}-date-${p.id}`} className={filters.date_range === p.id ? "period-chip active" : "period-chip"} onClick={() => set({ date_range: p.id })}>{p.label}</button>
      ))}
    </div>
    {filters.date_range === "custom" && <div className="custom-range">
      <label>From <input type="date" data-testid={`${testIdPrefix}-date-from`} value={filters.date_from} onChange={e => set({ date_from: e.target.value })} /></label>
      <label>To <input type="date" data-testid={`${testIdPrefix}-date-to`} value={filters.date_to} onChange={e => set({ date_to: e.target.value })} /></label>
    </div>}
    <div className="filter-selects">
      <label>Category
        <select data-testid={`${testIdPrefix}-category`} value={filters.category} onChange={e => set({ category: e.target.value })}>
          {CATEGORY_OPTIONS.map(o => <option key={o} value={o}>{o === "all" ? "All categories" : o.replaceAll("_", " ")}</option>)}
        </select>
      </label>
      <label>Source
        <select data-testid={`${testIdPrefix}-source`} value={filters.classification_source} onChange={e => set({ classification_source: e.target.value })}>
          {SOURCE_OPTIONS.map(o => <option key={o} value={o}>{o === "all" ? "All sources" : o.toUpperCase()}</option>)}
        </select>
      </label>
      <label>Stage
        <select data-testid={`${testIdPrefix}-stage`} value={filters.sales_stage} onChange={e => set({ sales_stage: e.target.value })}>
          {STAGE_OPTIONS.map(o => <option key={o} value={o}>{o === "all" ? "All stages" : o}</option>)}
        </select>
      </label>
      <label>Follow-up
        <select data-testid={`${testIdPrefix}-followup`} value={filters.follow_up} onChange={e => set({ follow_up: e.target.value })}>
          {FOLLOW_UP_OPTIONS.map(o => <option key={o.id} value={o.id}>{o.label}</option>)}
        </select>
      </label>
      <label>Agent
        <select data-testid={`${testIdPrefix}-agent`} value={filters.agent} onChange={e => set({ agent: e.target.value })}>
          <option value="all">All agents</option>
          {agents.filter(a => a).map(a => <option key={a} value={a}>{a}</option>)}
        </select>
      </label>
      <button data-testid={`${testIdPrefix}-reset`} className="button button-ghost" onClick={() => setFilters(emptyFilters())}>Reset</button>
    </div>
  </div>;
}

function toApiParams(filters) {
  const params = {};
  if (filters.search) params.search = filters.search;
  if (filters.date_range && filters.date_range !== "all") params.date_range = filters.date_range;
  if (filters.date_range === "custom") {
    if (filters.date_from) params.date_from = filters.date_from;
    if (filters.date_to) params.date_to = filters.date_to;
  }
  if (filters.category && filters.category !== "all") params.category = filters.category;
  if (filters.classification_source && filters.classification_source !== "all") params.classification_source = filters.classification_source;
  if (filters.sales_stage && filters.sales_stage !== "all") params.sales_stage = filters.sales_stage;
  if (filters.follow_up && filters.follow_up !== "all") params.follow_up = filters.follow_up;
  if (filters.agent && filters.agent !== "all") params.agent = filters.agent;
  return params;
}

// -------- Active Customers view --------

const REMOVE_REASONS = [
  { value: "wrongly_classified", label: "Wrongly classified" },
  { value: "no_purchase_made", label: "No purchase made" },
  { value: "duplicate", label: "Duplicate" },
  { value: "other", label: "Other" },
];

function ActiveCustomers() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [filters, setFilters] = useSessionFilters("active-customer-filters");
  const [removing, setRemoving] = useState(null); // lead being removed (opens modal)

  const load = () => {
    setLoading(true); setError("");
    axios.get(`${API}/active-customers`, { params: toApiParams(filters) })
      .then(res => setRows(res.data || []))
      .catch(err => setError(err.response?.data?.detail || "Could not load active customers"))
      .finally(() => setLoading(false));
  };
  useEffect(load, [filters]);

  const agents = [...new Set(rows.map(r => r.archived_by).filter(Boolean))].sort();
  return <>
    <PageHeader eyebrow="WORKSPACE / CUSTOMERS" title="Active customers" subtitle="Manually converted customers stay forever. Auto-captured active customers are silently archived after 3 days by a background job." action={<Badge tone="green" data-testid="active-customers-count">{rows.length} customer{rows.length === 1 ? "" : "s"}</Badge>} />
    <ListFilters filters={filters} setFilters={setFilters} agents={agents} testIdPrefix="active-filter" />
    {error && <div className="funnel-error" data-testid="active-error">{error}</div>}
    <section className="panel active-panel">
      <div className="table-wrap">
        <table data-testid="active-customers-table">
          <thead><tr><th>Customer</th><th>Category</th><th>AI confidence</th><th>Source</th><th>Marked active</th><th>Agent</th><th></th></tr></thead>
          <tbody>
            {loading ? <tr><td colSpan="7" className="funnel-loading">Loading…</td></tr>
              : rows.length === 0 ? <tr><td colSpan="7" className="funnel-loading">No active customers match your filters.</td></tr>
              : rows.map(r => (
                <tr key={r.lead_id} data-testid={`active-row-${r.lead_id}`}>
                  <td><div className="table-lead"><div className="lead-mini-avatar">{initials(r.customer_name)}</div><div><strong>{r.customer_name}</strong><span>{r.phone_number}</span></div></div></td>
                  <td>{r.category ? <Badge tone={r.category === "marketing_lead" ? "purple" : "blue"}>{r.category.replaceAll("_", " ")}</Badge> : <span className="muted">—</span>}</td>
                  <td data-testid={`active-confidence-${r.lead_id}`}>{typeof r.ai_confidence === "number" ? `${Math.round(r.ai_confidence * 100)}%` : "—"}</td>
                  <td>{r.classification_source ? <Badge tone={r.classification_source === "llm" ? "amber" : "slate"}>{r.classification_source.toUpperCase()}</Badge> : "—"}</td>
                  <td>{r.archived_at ? new Date(r.archived_at).toLocaleString() : "—"}</td>
                  <td>{r.archived_by || "—"}</td>
                  <td><button data-testid={`remove-active-${r.lead_id}`} className="button button-danger" onClick={() => setRemoving(r)}>Remove from active</button></td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>
    </section>
    {removing && <RemoveActiveModal lead={removing} onClose={() => setRemoving(null)} onDone={() => { setRemoving(null); load(); }} />}
  </>;
}

// ExpiringActiveBanner removed per PRD 2026-03-05 — the 12h background purge for auto-captured
// active customers now runs silently with no user-facing control.

function RemoveActiveModal({ lead, onClose, onDone }) {
  const [reason, setReason] = useState("wrongly_classified");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const submit = async () => {
    setBusy(true); setErr("");
    try {
      await axios.post(`${API}/active-customers/${lead.lead_id}/remove`, { reason, note });
      onDone();
    } catch (e) {
      setErr(e.response?.data?.detail || "Could not remove customer");
    } finally { setBusy(false); }
  };
  return <div className="modal-backdrop" data-testid="remove-active-modal" onClick={onClose}>
    <div className="modal-card" onClick={e => e.stopPropagation()}>
      <div className="modal-title"><Trash2 size={16} /> Remove {lead.customer_name} from active</div>
      <p className="muted">The lead will move back to the pipeline with status <b>NEW LEAD</b>. Your reason is stored as a labelled example so the AI learns from it.</p>
      <label className="modal-label">Reason
        <select data-testid="remove-reason" value={reason} onChange={e => setReason(e.target.value)}>
          {REMOVE_REASONS.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
        </select>
      </label>
      <label className="modal-label">Additional note (optional)
        <textarea data-testid="remove-note" value={note} onChange={e => setNote(e.target.value)} placeholder="e.g. contact never confirmed order" rows="3" />
      </label>
      {err && <div className="funnel-error" data-testid="remove-error">{err}</div>}
      <div className="modal-actions">
        <button className="button button-ghost" onClick={onClose} disabled={busy}>Cancel</button>
        <button data-testid="remove-confirm" className="button button-danger" onClick={submit} disabled={busy}>{busy ? "Removing…" : "Confirm remove"}</button>
      </div>
    </div>
  </div>;
}


// -------- Lost leads view (uses shared ListFilters) --------
// Lost-reason labels are the canonical PRD 2026-03-05 master list — imported from above.
const LOST_REASON_LABELS = LOST_REASON_LABEL;

function LostLeads() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [filters, setFilters] = useSessionFilters("lost-leads-filters");
  const [reasonFilter, setReasonFilter] = useState("all");
  const load = () => {
    setLoading(true); setError("");
    const params = { ...toApiParams(filters) };
    if (reasonFilter !== "all") params.lost_reason = reasonFilter;
    axios.get(`${API}/lost-leads`, { params })
      .then(r => setRows(r.data || []))
      .catch(err => setError(err.response?.data?.detail || "Could not load lost leads"))
      .finally(() => setLoading(false));
  };
  useEffect(load, [filters, reasonFilter]);
  const [exportBusy, setExportBusy] = useState(false);
  const exportCsv = async () => {
    setExportBusy(true);
    try {
      const params = { ...toApiParams(filters) };
      if (reasonFilter !== "all") params.lost_reason = reasonFilter;
      const r = await axios.get(`${API}/lost-leads/export`, { params, responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `paushtikk-lost-leads-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      setError(err.response?.data?.detail || "Could not export lost leads");
    } finally {
      setExportBusy(false);
    }
  };
  const agents = [...new Set(rows.map(r => r.archived_by).filter(Boolean))].sort();
  return <>
    <PageHeader eyebrow="WORKSPACE / LOST" title="Lost leads" subtitle="Deals that didn't convert. Use the reasons here to sharpen future outreach." action={<div className="page-header-actions"><Badge tone="red" data-testid="lost-leads-count">{rows.length} lost</Badge><button data-testid="export-lost-leads-button" className="button button-ghost" onClick={exportCsv} disabled={exportBusy || rows.length === 0}><Zap size={15} /> {exportBusy ? "Exporting…" : "Export CSV"}</button></div>} />
    <ListFilters filters={filters} setFilters={setFilters} agents={agents} testIdPrefix="lost-filter" />
    <div className="lost-reason-filter" data-testid="lost-reason-filter">
      <label>Lost reason
        <select data-testid="lost-filter-reason" value={reasonFilter} onChange={e => setReasonFilter(e.target.value)}>
          <option value="all">All reasons</option>
          {Object.entries(LOST_REASON_LABELS).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
        </select>
      </label>
    </div>
    {error && <div className="funnel-error" data-testid="lost-error">{error}</div>}
    <section className="panel active-panel">
      <div className="table-wrap">
        <table data-testid="lost-leads-table">
          <thead><tr><th>Customer</th><th>Reason</th><th>Notes</th><th>Category</th><th>Marked lost</th><th>Agent</th></tr></thead>
          <tbody>
            {loading ? <tr><td colSpan="6" className="funnel-loading">Loading…</td></tr>
              : rows.length === 0 ? <tr><td colSpan="6" className="funnel-loading">No lost leads match your filters.</td></tr>
              : rows.map(r => (
                <tr key={r.lead_id} data-testid={`lost-row-${r.lead_id}`}>
                  <td><div className="table-lead"><div className="lead-mini-avatar">{initials(r.customer_name)}</div><div><strong>{r.customer_name}</strong><span>{r.phone_number}</span></div></div></td>
                  <td>{r.lost_reason ? <Badge tone="red">{LOST_REASON_LABELS[r.lost_reason] || r.lost_reason}</Badge> : "—"}</td>
                  <td className="muted" style={{ maxWidth: 280 }}>{r.lost_notes || "—"}</td>
                  <td>{r.category ? <Badge tone={r.category === "marketing_lead" ? "purple" : "blue"}>{r.category.replaceAll("_", " ")}</Badge> : <span className="muted">—</span>}</td>
                  <td>{r.archived_at ? new Date(r.archived_at).toLocaleString() : "—"}</td>
                  <td>{r.archived_by || "—"}</td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>
    </section>
  </>;
}

// -------- First-time-user onboarding walkthrough --------

const ONBOARDING_STEPS = [
  { id: "leads", page: "leads", selector: '[data-testid="nav-leads"]', title: "1 · New Leads", body: "Every inbound WhatsApp conversation lands here first. Priority, category, and score are set automatically." },
  { id: "active", page: "active", selector: '[data-testid="nav-active"]', title: "2 · Active Customers", body: "Contacts marked as customers live here. Auto-captured ones are kept for 3 days; the ones you mark are kept forever." },
  { id: "mark-active", page: "leads", selector: '[data-testid="mark-active-button"]', title: "3 · Mark a lead as active", body: "Open any lead and hit this button to convert. You'll be asked for the deal value and who closed it — the LLM learns from that." },
  { id: "filters", page: "leads", selector: '[data-testid="leads-filter-panel"]', title: "4 · Filters", body: "Slice any list by date, category, source, agent, stage, or follow-up. Filters stick during your session." },
  { id: "followup", page: "dashboard", selector: '[data-testid="follow-up-picker"], .follow-up-picker, .followup-panel', title: "5 · Follow-up date", body: "Pick a date on the Overview to see everyone due that day. Overdue leads are highlighted in red." },
  { id: "notifications", page: "dashboard", selector: '[data-testid="expiring-active-banner"], .banner, [data-testid="follow-up-banner"]', title: "6 · Notifications", body: "Time-sensitive alerts (auto-expiring customers, overdue follow-ups) show at the top of your screen. Nothing gets buried." },
];

function OnboardingWalkthrough({ onGoto }) {
  const [step, setStep] = useState(-1); // -1 = not started
  const [rect, setRect] = useState(null);
  const [needsShow, setNeedsShow] = useState(false);

  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/users/me/onboarding`).then(r => {
      if (cancelled) return;
      if (!r.data?.completed) { setNeedsShow(true); setStep(0); }
    }).catch(() => { /* fail-open — don't block the app */ });
    const onReplay = () => { setNeedsShow(true); setStep(0); };
    window.addEventListener("onboarding:replay", onReplay);
    return () => { cancelled = true; window.removeEventListener("onboarding:replay", onReplay); };
  }, []);

  useEffect(() => {
    if (step < 0 || step >= ONBOARDING_STEPS.length) return;
    const s = ONBOARDING_STEPS[step];
    onGoto(s.page);
    const t = setTimeout(() => {
      const el = document.querySelector(s.selector);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
        setRect(el.getBoundingClientRect());
      } else {
        // Target not on screen — center the tooltip
        setRect(null);
      }
    }, 350);
    return () => clearTimeout(t);
  }, [step, onGoto]);

  const finish = async (completed) => {
    setNeedsShow(false); setStep(-1); setRect(null);
    try { await axios.post(`${API}/users/me/onboarding`, { completed }); } catch (_) { /* ignore */ }
  };

  if (!needsShow || step < 0 || step >= ONBOARDING_STEPS.length) return null;
  const s = ONBOARDING_STEPS[step];
  const style = rect ? {
    top: Math.min(window.innerHeight - 220, rect.bottom + 14),
    left: Math.min(window.innerWidth - 340, Math.max(16, rect.left)),
  } : { top: "40%", left: "50%", transform: "translateX(-50%)" };
  const highlightStyle = rect ? { top: rect.top - 6, left: rect.left - 6, width: rect.width + 12, height: rect.height + 12 } : null;

  return <div className="onboarding-overlay" data-testid="onboarding-overlay">
    {highlightStyle && <div className="onboarding-highlight" style={highlightStyle} />}
    <div className="onboarding-tooltip" style={style} data-testid={`onboarding-step-${s.id}`}>
      <div className="onboarding-progress">Step {step + 1} of {ONBOARDING_STEPS.length}</div>
      <h4>{s.title}</h4>
      <p>{s.body}</p>
      <div className="onboarding-actions">
        <button data-testid="onboarding-skip" className="button button-ghost" onClick={() => finish(true)}>Skip tutorial</button>
        {step < ONBOARDING_STEPS.length - 1
          ? <button data-testid="onboarding-next" className="button button-primary" onClick={() => setStep(s => s + 1)}>Next</button>
          : <button data-testid="onboarding-done" className="button button-primary" onClick={() => finish(true)}>Finish</button>}
      </div>
    </div>
  </div>;
}

